import Airtable from 'airtable';
import * as Sentry from '@sentry/react';
// Token rotated 2026-04-09
import { getPreloadedSessions, getPreloadedScheduledOfferings, getPreloadedSOsForSearch, PRELOAD_CACHE_KEYS } from './backgroundPreloader.js';

// Validate environment variables - support both Vite (browser) and Node.js environments
const requiredEnvVars = {
  VITE_AIRTABLE_ACCESS_TOKEN: (typeof import.meta !== 'undefined' && import.meta.env) 
    ? import.meta.env.VITE_AIRTABLE_ACCESS_TOKEN 
    : process.env.VITE_AIRTABLE_ACCESS_TOKEN,
  VITE_AIRTABLE_OPERATIONS_BASE_ID: (typeof import.meta !== 'undefined' && import.meta.env)
    ? import.meta.env.VITE_AIRTABLE_OPERATIONS_BASE_ID
    : process.env.VITE_AIRTABLE_OPERATIONS_BASE_ID
};

// Check for missing environment variables
const missingEnvVars = Object.entries(requiredEnvVars)
  .filter(([, value]) => !value)
  .map(([key]) => key);

if (missingEnvVars.length > 0) {
  throw new Error(
    `Missing required environment variables: ${missingEnvVars.join(', ')}. ` +
    'Please check your Cloudflare Pages environment variables configuration.'
  );
}

// Initialize base

const operationsBase = new Airtable({
  apiKey: requiredEnvVars.VITE_AIRTABLE_ACCESS_TOKEN,
  endpointUrl: 'https://api.airtable.com'
}).base(requiredEnvVars.VITE_AIRTABLE_OPERATIONS_BASE_ID);

// Constants for table names
const TABLES = {
  SCHEDULED_OFFERINGS: 'Scheduled Offerings',
  SESSIONS: 'Sessions',
  ATTENDEES: 'Attendees',
  SESSION_ATTENDANCE: 'Session Attendance',
  CLIENTS: 'Clients',
  PROGRAMS: 'Programs',
  FAQS: 'FAQs',
  EXTERNAL_SURVEY_IMPORTS: 'External Survey Imports',
  EXTERNAL_SURVEY_RESPONSES: 'External Survey Responses',
  EXTERNAL_SURVEY_SO_ASSOCIATIONS: 'External Survey SO Associations',
  // Materials Management Tables
  MATERIAL_VERSIONS: 'Material_Versions',
  MATERIAL_FILES: 'Material_Files',
  MATERIAL_ACCESS_LOGS: 'Material_Access_Logs',
  MATERIAL_BULK_OPERATIONS: 'Material_Bulk_Operations'
};

// Export operationsBase and TABLES for use in backgroundPreloader
export { operationsBase, TABLES };

// Helper function to escape user input for Airtable formula strings
export const escapeAirtableString = (str) => {
  if (typeof str !== 'string') {
    return '';
  }
  
  // Additional validation for client names
  if (str.length > 200) {
    throw new Error('Input string too long for Airtable formula');
  }
  
  return str
    .replace(/\\/g, '\\\\')  // Escape backslashes first
    .replace(/'/g, "\\'")    // Escape single quotes (critical for formula injection)
    .replace(/"/g, '\\"')    // Escape double quotes
    .replace(/,/g, '\\,')    // Escape commas (critical for Airtable formula parsing)
    .replace(/\n/g, '\\n')   // Escape newlines
    .replace(/\r/g, '\\r')   // Escape carriage returns
    .replace(/\t/g, '\\t');  // Escape tabs
};


// Rate limiting helper for Airtable API (5 req/sec limit)
const rateLimitDelay = () => new Promise(resolve => setTimeout(resolve, 220)); // 220ms = ~4.5 req/sec with safety margin

// Retry logic for rate limit errors
const retryWithBackoff = async (apiCall, operation, maxRetries = 3) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error) {
      // Check if this is a rate limit error (429) or connection issue
      const isRateLimited = error.statusCode === 429 ||
                           error.message?.includes('rate') ||
                           error.message?.includes('Too Many Requests');
      const isConnectionError = error.code === 'ECONNRESET' ||
                               error.code === 'ENOTFOUND' ||
                               error.message?.includes('network');

      if ((isRateLimited || isConnectionError) && attempt < maxRetries) {
        const backoffDelay = Math.min(1000 * Math.pow(2, attempt - 1), 10000); // Exponential backoff, max 10s
        console.warn(`⚠️ ${operation}: Rate limited/connection error (attempt ${attempt}/${maxRetries}). Retrying in ${backoffDelay}ms...`);
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        continue;
      }
      throw error;
    }
  }
};

/**
 * Validate an API response for basic structural correctness.
 * Accepts both raw Airtable records (with .get() method) and transformed plain objects.
 * @param {any} data - The API response to validate
 * @param {string} operation - Short description/operation name used in warning messages
 * @param {string[]} [requiredFields=[]] - Array of required property names to check
 * @returns {boolean} false when data is null/undefined or records are invalid, true when validation passes
 */
const validateApiResponse = (data, operation, requiredFields = []) => {
  if (!data) {
    console.warn(`⚠️ ${operation}: API returned null/undefined data`);
    return false;
  }
  
  if (Array.isArray(data) && data.length === 0) {
    // Empty arrays are valid
    return true;
  }
  
  if (Array.isArray(data)) {
    // Validate first few records for required fields
    const sampleSize = Math.min(3, data.length);
    for (let i = 0; i < sampleSize; i++) {
      const record = data[i];
      // Accept both raw Airtable records (have .get()) and transformed plain objects
      const isRawRecord = record != null && record.get && typeof record.get === 'function';
      const isPlainObject = record !== null && typeof record === 'object' && Object.keys(record).length > 0;
      if (!isRawRecord && !isPlainObject) {
        console.warn(`⚠️ ${operation}: Record ${i} is not a valid record or plain object`);
        return false;
      }
      // Check if required fields exist (if provided)
      for (const field of requiredFields) {
        // Field validation would go here if needed
      }
    }
  }
  
  return true;
};

// Helper function to track API calls and handle errors with rate limiting
const trackApiCall = async (operation, data, apiCall) => {
  const startTime = performance.now();
  
  try {
    Sentry.addBreadcrumb({
      message: `Airtable API: ${operation}`,
      category: 'api',
      level: 'info',
      data: data
    });

    // Apply rate limiting and retry logic
    const result = await retryWithBackoff(async () => {
      await rateLimitDelay();
      return await apiCall();
    }, operation);
    
    const duration = performance.now() - startTime;
    
    // Validate API response
    if (!validateApiResponse(result, operation)) {
      console.warn(`⚠️ ${operation}: API response validation failed`);
    }
    
    Sentry.addBreadcrumb({
      message: `Airtable API ${operation} completed`,
      category: 'api',
      level: 'info',
      data: { 
        duration: Math.round(duration), 
        success: true,
        recordCount: Array.isArray(result) ? result.length : 1
      }
    });

    // Log slow operations for performance monitoring
    if (duration > 5000) {
      console.warn(`⚠️ Slow API call: ${operation} took ${duration.toFixed(2)}ms`);
    }

    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    console.error(`❌ Airtable API ${operation} failed after ${duration.toFixed(2)}ms:`, error);

    Sentry.withScope((scope) => {
      scope.setTag('apiOperation', operation);
      scope.setContext('apiCall', {
        operation,
        duration: Math.round(duration),
        data: data,
        errorMessage: error.message,
        statusCode: error.statusCode
      });
      
      // Include SO numbers for debugging - internal business identifiers
      if (data?.soNumber) {
        scope.setContext('searchContext', {
          soNumber: data.soNumber
        });
      }
      
      Sentry.captureException(error);
    });
    
    throw error;
  }
};

// Fetch program data from Operations base
export const fetchProgramBySO = async (soNumber) => {
  return trackApiCall('fetchProgramBySO', { soNumber }, async () => {
    
    // Try to get SO from preloaded cache first
    const preloadedSOs = getPreloadedSOsForSearch(soNumber);
    let soRecords = [];
    
    if (preloadedSOs && preloadedSOs.length > 0) {
      console.log(`💾 Using preloaded SO data for ${soNumber}`);
      // Convert preloaded data back to Airtable-like records
      soRecords = preloadedSOs.map(so => ({
        id: so.id,
        fields: so.fields,
        get: (fieldName) => so.fields[fieldName]
      }));
    } else {
      // Fetch from Airtable API if not in cache
      console.log(`🔄 Fetching SO ${soNumber} from API (not in preload cache)`);
      const escapedSoNumber = escapeAirtableString(soNumber);
      soRecords = await operationsBase(TABLES.SCHEDULED_OFFERINGS)
        .select({
          filterByFormula: `{SO} = '${escapedSoNumber}'`,
          maxRecords: 1
        })
        .firstPage();
    }


    if (!soRecords || soRecords.length === 0) {
      throw new Error(`Scheduled Offering with SO number "${soNumber}" not found. Please verify the SO number is correct.`);
    }

    const soRecord = soRecords[0];
    const soid = soRecord.get('SOID');

    // Debug warning for misconfigured SOs with multiple programs (commas in SOID)
    if (soid && soid.includes(',')) {
      console.warn(`⚠️ SO ${soNumber} has multiple programs linked (SOID contains comma): "${soid}". This may cause attendance persistence issues. Contact Tara to fix the SO configuration.`);
    }

    // Return basic program data without sessions - we'll fetch sessions separately
    return {
      so: soRecord.get('SO'),
      soid: soid,
      deliveryStart: soRecord.get('Delivery Start'),
      deliveryEnd: soRecord.get('Delivery End'),
      programName: soRecord.get('ProgramName'),
      lang: soRecord.get('Lang'),
      virtualPlatform: soRecord.get('Virtual Platform'),
      plName: soRecord.get('PL Name'),
      // Normalize lookup (may be array) to single string
      clientName: (() => {
        const v = soRecord.get('Client Name');
        return Array.isArray(v) ? (v[0] || '') : (v || '');
      })(),
      type: soRecord.get('Type'),
      status: soRecord.get('SO Status'),
      soNotes: soRecord.get('PL Notes') || '',
      sessions: [], // Will be populated separately
      // Add full SO record for conditional client dropdown
      soRecord: {
        id: soRecord.id,
        fields: {
          SO: soRecord.get('SO'),
          Client: soRecord.get('Client'), // Record ID or "OPEN-PROGRAM" string
          'Client Name': soRecord.get('Client Name'),
          'Participating Clients': soRecord.get('Participating Clients') // Array of client record IDs for open programs
        }
      }
    };
  });
};


// Fetch sessions for a specific SO number
export const fetchSessions = async (soNumber) => {
  return trackApiCall('fetchSessions', { soNumber }, async () => {
    const sessionRecords = await operationsBase(TABLES.SESSIONS)
      .select({
        filterByFormula: `{SO (from SOID)} = "${soNumber}"`,
        sort: [{ field: 'Start', direction: 'asc' }]
      })
      .all();


    return sessionRecords.map(record => ({
      sessionId: record.id,
      sessionNo: record.get('Session No'),
      sessionName: record.get('Session Name'),
      start: record.get('Start'),
      sessionStatus: record.get('Session Status'),
      meetingLink: record.get('Meeting Link URL'),
      meetingPassword: record.get('Meeting Password'),
      meetingId: record.get('Meeting ID'),
      // Materials version fields for conditional rendering
      so_materials_version: record.get('so_materials_version'),
      master_materials_version: record.get('master_materials_version'),
      // Normalize potential Airtable lookup/array to a single string
      surveyLink: (() => {
        const v = record.get('Survey Link TRUTH');
        return Array.isArray(v) ? (v[0] || '') : (v || '');
      })()
    }));
  });
};

// Fetch attendees for a specific Scheduled Offering
export const fetchAttendees = async (soid, soNumber) => {
  return trackApiCall('fetchAttendees', { soid, soNumber }, async () => {
    
    // First, fetch attendees by SO number, sessions, and SO client info in parallel
    const escapedSoNumber = escapeAirtableString(soNumber);
    const [directAttendeeRecords, sessionRecords, soRecords] = await Promise.all([
      operationsBase(TABLES.ATTENDEES)
        .select({
          filterByFormula: `SEARCH("${escapedSoNumber}", {SO})`
        })
        .all(),
      operationsBase(TABLES.SESSIONS)
        .select({
          filterByFormula: `{SO (from SOID)} = "${escapedSoNumber}"`
        })
        .all(),
      operationsBase(TABLES.SCHEDULED_OFFERINGS)
        .select({
          filterByFormula: `{SOID} = '${soid}'`,
          fields: ['Client', 'Client Name']
        })
        .all()
    ]);
    
    // Get SO client information
    let soClientName = '';
    if (soRecords.length > 0) {
      const soRecord = soRecords[0];
      const clientNameValue = soRecord.get('Client Name');
      
      // Normalize Client Name lookup to string (may be array)
      if (Array.isArray(clientNameValue)) {
        soClientName = clientNameValue.join(', ');
      } else {
        soClientName = String(clientNameValue || '');
      }
      // If Client Name lookup fails, we could fetch the client record here if needed
    }
    
    // Create Sets of valid IDs for validation
    const validAttendeeIds = new Set(directAttendeeRecords.map(r => r.id));
    const validSessionIds = new Set(sessionRecords.map(r => r.id));
    
      
    Sentry.addBreadcrumb({
      message: `Initial fetch results - Attendees: ${directAttendeeRecords.length}, Sessions: ${sessionRecords.length}`,
      category: 'data-fetch',
      level: 'info',
      data: { soid, soNumber, attendeeCount: directAttendeeRecords.length, sessionCount: sessionRecords.length }
    });

    if (directAttendeeRecords.length === 0) {
      Sentry.addBreadcrumb({
        message: 'No attendees found for SO number',
        category: 'data-fetch',
        level: 'warning',
        data: { soNumber, soid }
      });
      return [];
    }
    
    if (sessionRecords.length === 0) {
      Sentry.addBreadcrumb({
        message: 'No sessions found for SOID',
        category: 'data-fetch', 
        level: 'warning',
        data: { soid, soNumber }
      });
      return [];
    }
    
    // Efficient approach: Direct SOID filtering (revert to main branch approach)
    const escapedSoid = escapeAirtableString(soid);
    const relevantAttendanceRecords = await operationsBase(TABLES.SESSION_ATTENDANCE)
      .select({
        filterByFormula: `{SOID (from Session)} = "${escapedSoid}"`,
        // No maxRecords limit - fetch all attendance records for this SOID
        // With server-side filtering, we only get relevant records
      })
      .all();
    
    Sentry.addBreadcrumb({
      message: `Found attendance records using efficient SOID filtering`,
      category: 'data-fetch',
      level: 'info', 
      data: { soid, attendanceRecordCount: relevantAttendanceRecords.length }
    });
    
    // Create a map of session attendance
    const orphanedRecords = [];
    const attendanceMap = relevantAttendanceRecords.reduce((map, record) => {
      // Check if the attendance record has valid links
      const attendeeLinks = record.get('Attendee');
      const sessionLinks = record.get('Session');
      
      // Skip orphaned records (missing attendee or session links, or links to deleted records)
      if (!attendeeLinks || !attendeeLinks[0] || !sessionLinks || !sessionLinks[0]) {
        orphanedRecords.push({
          recordId: record.id,
          hasAttendee: !!(attendeeLinks && attendeeLinks[0]),
          hasSession: !!(sessionLinks && sessionLinks[0])
        });
        return map;
      }
      
      const attendeeId = attendeeLinks[0];
      const sessionId = sessionLinks[0];
      
      // Check if the linked attendee and session still exist
      if (!validAttendeeIds.has(attendeeId) || !validSessionIds.has(sessionId)) {
        orphanedRecords.push({
          recordId: record.id,
          hasAttendee: !!(attendeeLinks && attendeeLinks[0]),
          hasSession: !!(sessionLinks && sessionLinks[0]),
          attendeeExists: validAttendeeIds.has(attendeeId),
          sessionExists: validSessionIds.has(sessionId)
        });
        return map;
      }
      const present = record.get('Present') || false;
      const notes = record.get('Notes') || '';
      
      if (!map[attendeeId]) {
        map[attendeeId] = {
          sessions: {},
          attendanceRecords: {},
          sessionNotes: {}
        };
      }
      
      // Handle duplicates - keep the most recent record based on Modified timestamp
      const existingRecordId = map[attendeeId].attendanceRecords[sessionId];
      if (existingRecordId) {
        // We have a duplicate - compare Modified timestamps
        const existingRecord = relevantAttendanceRecords.find(r => r.id === existingRecordId);
        const existingModified = existingRecord?.get('Modified');
        const currentModified = record.get('Modified');
        
        // If current record is not newer, skip it
        if (existingModified && currentModified) {
          if (new Date(existingModified) >= new Date(currentModified)) {
            return map; // Keep existing record as it's newer or same age
          }
        }
        
        Sentry.addBreadcrumb({
          message: `Duplicate attendance found for attendee ${attendeeId} session ${sessionId} - using most recent`,
          category: 'data',
          level: 'warning'
        });
      }
      
      // Store this record (either it's the only one or it's more recent)
      map[attendeeId].sessions[sessionId] = present;
      map[attendeeId].attendanceRecords[sessionId] = record.id;
      map[attendeeId].sessionNotes[sessionId] = notes;
      
      
      return map;
    }, {});
    
    // Log orphaned records if any were found
    if (orphanedRecords.length > 0) {
      Sentry.withScope((scope) => {
        scope.setLevel('warning');
        scope.setExtras({
          soid,
          orphanedCount: orphanedRecords.length,
          orphanedRecords: orphanedRecords.slice(0, 10) // Log first 10 for debugging
        });
        Sentry.captureMessage('orphanedAttendanceRecords');
      });
    }
    
    // Filter out cancelled attendees first
    const filteredAttendeeRecords = directAttendeeRecords
      .filter(record => {
        // Filter out cancelled attendees - normalize status to lowercase for comparison
        const status = (record.get('Status') || '').toLowerCase();
        return status !== 'cancelled';
      });

    // Collect unique client IDs to batch fetch client names
    const clientIds = [...new Set(
      filteredAttendeeRecords
        .map(record => {
          const clientInfo = record.get('Client') || [];
          return clientInfo.length > 0 ? clientInfo[0] : null;
        })
        .filter(Boolean)
    )];

    // Batch fetch client names if we have client IDs
    let clientNamesMap = {};
    if (clientIds.length > 0) {
      try {
        const clientRecords = await operationsBase('Clients')
          .select({
            filterByFormula: `OR(${clientIds.map(id => `RECORD_ID() = '${id}'`).join(', ')})`,
            fields: ['Client Long Name']
          })
          .all();
        
        clientNamesMap = clientRecords.reduce((map, record) => {
          map[record.id] = record.get('Client Long Name') || '';
          return map;
        }, {});
      } catch (error) {
        Sentry.addBreadcrumb({
          message: 'Error fetching client names',
          category: 'data-fetch',
          level: 'warning',
          data: { clientIds, error: error.message }
        });
      }
    }

    // CRITICAL FIX: Only show attendees who have session attendance records
    // Filter to attendees that exist in attendanceMap (have session attendance)
    const attendeesWithAttendance = filteredAttendeeRecords
      .filter(record => attendanceMap[record.id]); // Only include if they have attendance records
    
    // Combine attendee details with their attendance and client names
    const result = attendeesWithAttendance
      .map(record => {
        const attendeeId = record.id;
        const attendance = attendanceMap[attendeeId]; // No fallback needed - we know it exists
        
        // Get client info if available, fallback to SO client
        const clientInfo = record.get('Client') || [];
        const clientId = clientInfo.length > 0 ? clientInfo[0] : null;
        const attendeeClientName = clientId ? clientNamesMap[clientId] || '' : '';
        
        // For OPEN-PROGRAM SOs, always use individual Company Name field
        // For other SOs, use attendee's linked client or fallback to SO client
        const effectiveClientName = soClientName === 'OPEN-PROGRAM' 
          ? record.get('Company Name') || ''
          : attendeeClientName || soClientName;
        
        return {
          attendeeId: record.id,
          firstName: record.get('First Name'),
          lastName: record.get('Last Name'),
          email: record.get('Email') || '',
          notes: record.get('Notes') || '',
          clientId: clientId,
          clientName: effectiveClientName,
          attendeeClientName: attendeeClientName, // Original attendee client (for debugging)
          soClientName: soClientName, // SO client fallback (for debugging)
          sessions: attendance.sessions,
          attendanceRecords: attendance.attendanceRecords,
          sessionNotes: attendance.sessionNotes
        };
      });
    
    return result;
  });
};

// Fetch all active/test clients for dropdown selection (excluding complete, lost, IP Partner, dead to us)
export const fetchAllClients = async () => {
  return trackApiCall('fetchAllClients', {}, async () => {
    const clientRecords = await operationsBase('Clients')
      .select({
        fields: ['ClientID', 'Client Long Name', 'Status'],
        filterByFormula: `AND(
          OR({Status} = 'Active', {Status} = 'Test', {Status} = 'TEST', {Status} = 'test'),
          {Status} != 'Complete',
          {Status} != 'Lost',
          {Status} != 'IP Partner',
          {Status} != 'Dead to Us',
          {Client Long Name} != '',
          {Client Long Name} != BLANK()
        )`,
        sort: [{ field: 'Client Long Name', direction: 'asc' }]
      })
      .all();

    return clientRecords.map(record => ({
      id: record.id,
      clientId: record.get('ClientID'),
      name: record.get('Client Long Name'),
      status: record.get('Status')
    }));
  });
};

/**
 * Fetch specific clients by their Airtable record IDs
 * Used for populating participating clients dropdown for open programs
 * @param {string[]} clientIds - Array of Airtable record IDs (e.g., ['recXXX', 'recYYY'])
 * @returns {Promise<Array>} Array of client objects with id, clientId, name, status
 */
export const fetchClientsByIds = async (clientIds) => {
  return trackApiCall('fetchClientsByIds', { clientIds }, async () => {
    // Handle empty array gracefully
    if (!Array.isArray(clientIds) || clientIds.length === 0) {
      return [];
    }

    // Validate clientIds are strings
    const validIds = clientIds.filter(id => typeof id === 'string' && id.trim().length > 0);
    if (validIds.length === 0) {
      return [];
    }

    // Build filterByFormula using OR(RECORD_ID()='recXXX', RECORD_ID()='recYYY', ...)
    // This fetches ONLY the specific clients by their record IDs
    const recordIdConditions = validIds.map(id => `RECORD_ID()='${id}'`).join(', ');
    const filterFormula = `AND(OR(${recordIdConditions}), {Status} = 'Active', {Client Long Name} != '', {Client Long Name} != BLANK())`;

    const clientRecords = await operationsBase('Clients')
      .select({
        fields: ['ClientID', 'Client Long Name', 'Status'],
        filterByFormula: filterFormula,
        sort: [{ field: 'Client Long Name', direction: 'asc' }]
      })
      .all();

    return clientRecords.map(record => ({
      id: record.id,
      clientId: record.get('ClientID'),
      name: record.get('Client Long Name'),
      status: record.get('Status')
    }));
  });
};

export const fetchAllPrograms = async () => {
  return trackApiCall('fetchAllPrograms', {}, async () => {
    const programRecords = await operationsBase('Programs')
      .select({
        fields: ['Full Program Name', 'Status', 'Current', 'Clients'],
        filterByFormula: `AND({Full Program Name} != '', {Full Program Name} != BLANK(), {Current} = TRUE())`,
        sort: [{ field: 'Full Program Name', direction: 'asc' }]
      })
      .all();
    
    return programRecords.map(record => ({
      id: record.id,
      name: record.get('Full Program Name'),
      status: record.get('Status'),
      current: record.get('Current'),
      clientIds: record.get('Clients') || [] // Array of client record IDs
    }));
  });
};

export const fetchSOsByClientAndProgram = async (clientName, programName) => {
  return trackApiCall('fetchSOsByClientAndProgram', { clientName, programName }, async () => {
    // Build filter formula for client and program
    let filterFormula = `AND({SO} != '', {SO} != BLANK()`;
    
    if (clientName) {
      filterFormula += `, {Client Name} = '${clientName.replace(/'/g, "\\'")}'`;
    }
    
    if (programName) {
      filterFormula += `, {ProgramName} = '${programName.replace(/'/g, "\\'")}'`;
    }
    
    filterFormula += ')';
    
    const soRecords = await operationsBase('Scheduled Offerings')
      .select({
        fields: ['SO', 'SOID', 'Client Name', 'ProgramName', 'Type', 'Language', 'Delivery Start', 'SO Status'],
        filterByFormula: filterFormula,
        sort: [{ field: 'SO', direction: 'asc' }]
      })
      .all();
    
    return soRecords.map(record => ({
      id: record.id,
      so: record.get('SO'),
      soid: record.get('SOID'),
      clientName: record.get('Client Name'),
      programName: record.get('ProgramName'),
      type: record.get('Type'),
      language: record.get('Language'),
      deliveryStart: record.get('Delivery Start'),
      status: record.get('SO Status')
    }));
  });
};

// Update attendance for a specific session
export const updateAttendanceStatus = async (attendanceRecordId, present) => {
  return trackApiCall('updateAttendanceStatus', { attendanceRecordId, present }, async () => {
    
    // Check if record exists before updating
    try {
      await operationsBase(TABLES.SESSION_ATTENDANCE).find(attendanceRecordId);
    } catch (findError) {
      console.error('Error finding attendance record:', findError);
      throw new Error(`Attendance record ${attendanceRecordId} not found`);
    }
    
    // Update the record
    await operationsBase(TABLES.SESSION_ATTENDANCE).update(attendanceRecordId, {
      'Present': present
    });
    
    return true;
  });
};

// Helper function to create a simple hash for telemetry
const createClientNameHash = (clientName) => {
  if (!clientName) return null;
  let hash = 0;
  for (let i = 0; i < clientName.length; i++) {
    const char = clientName.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16);
};

// Lookup client by name to get client ID
export const lookupClientByName = async (clientName) => {
  return trackApiCall('lookupClientByName', {
    clientNameRedacted: true,
    clientNameHash: createClientNameHash(clientName)
  }, async () => {
    if (!clientName || !clientName.trim()) {
      console.log('🔍 lookupClientByName: Empty client name');
      return null;
    }

    const normalizedName = clientName.trim();
    const escapedName = escapeAirtableString(normalizedName);

    console.log('🔍 lookupClientByName: Starting lookup', {
      original: clientName,
      normalized: normalizedName,
      escaped: escapedName
    });

    // Tier 1: Exact match on Client Long Name (case-insensitive) - only active clients
    const exactLongNameFormula = `AND(UPPER({Client Long Name}) = UPPER("${escapedName}"), {Status} = 'Active')`;
    console.log('🔍 Tier 1 - Exact match on Client Long Name:', exactLongNameFormula);

    let clientRecords = await operationsBase(TABLES.CLIENTS)
      .select({
        filterByFormula: exactLongNameFormula,
        maxRecords: 1,
        fields: ['Client Long Name', 'ClientID', 'Status']
      })
      .firstPage();

    console.log('🔍 Tier 1 results:', clientRecords.length > 0 ? {
      found: true,
      name: clientRecords[0].get('Client Long Name'),
      clientId: clientRecords[0].get('ClientID')
    } : { found: false });

    if (clientRecords.length > 0) {
      const match = {
        id: clientRecords[0].id,
        name: clientRecords[0].get('Client Long Name'),
        clientId: clientRecords[0].get('ClientID'),
        matchType: 'exact-long-name'
      };
      console.log('🔍 Returning Tier 1 exact match:', match);
      return match;
    }

    // Tier 2: Exact match on ClientID (case-insensitive) - only active clients
    const exactClientIdFormula = `AND(UPPER({ClientID}) = UPPER("${escapedName}"), {Status} = 'Active')`;
    console.log('🔍 Tier 2 - Exact match on ClientID:', exactClientIdFormula);

    clientRecords = await operationsBase(TABLES.CLIENTS)
      .select({
        filterByFormula: exactClientIdFormula,
        maxRecords: 1,
        fields: ['Client Long Name', 'ClientID', 'Status']
      })
      .firstPage();

    console.log('🔍 Tier 2 results:', clientRecords.length > 0 ? {
      found: true,
      name: clientRecords[0].get('Client Long Name'),
      clientId: clientRecords[0].get('ClientID')
    } : { found: false });

    if (clientRecords.length > 0) {
      const match = {
        id: clientRecords[0].id,
        name: clientRecords[0].get('Client Long Name'),
        clientId: clientRecords[0].get('ClientID'),
        matchType: 'exact-client-id'
      };
      console.log('🔍 Returning Tier 2 exact ClientID match:', match);
      return match;
    }

    // Tier 3: Partial match on Client Long Name - only active clients
    const partialLongNameFormula = `AND(SEARCH(UPPER("${escapedName}"), UPPER({Client Long Name})), {Status} = 'Active')`;
    console.log('🔍 Tier 3 - Partial match on Client Long Name:', partialLongNameFormula);

    clientRecords = await operationsBase(TABLES.CLIENTS)
      .select({
        filterByFormula: partialLongNameFormula,
        maxRecords: 5,
        fields: ['Client Long Name', 'ClientID', 'Status']
      })
      .firstPage();

    console.log('🔍 Tier 3 results:', clientRecords.length > 0 ? {
      found: true,
      count: clientRecords.length,
      matches: clientRecords.map(r => r.get('Client Long Name'))
    } : { found: false });

    if (clientRecords.length > 0) {
      const match = {
        id: clientRecords[0].id,
        name: clientRecords[0].get('Client Long Name'),
        clientId: clientRecords[0].get('ClientID'),
        isPartialMatch: true,
        matchType: 'partial-long-name',
        allMatches: clientRecords.map(r => ({
          id: r.id,
          name: r.get('Client Long Name'),
          clientId: r.get('ClientID')
        }))
      };
      console.log('🔍 Returning Tier 3 partial match:', match);
      return match;
    }

    // Tier 4: Partial match on ClientID - only active clients
    const partialClientIdFormula = `AND(SEARCH(UPPER("${escapedName}"), UPPER({ClientID})), {Status} = 'Active')`;
    console.log('🔍 Tier 4 - Partial match on ClientID:', partialClientIdFormula);

    clientRecords = await operationsBase(TABLES.CLIENTS)
      .select({
        filterByFormula: partialClientIdFormula,
        maxRecords: 5,
        fields: ['Client Long Name', 'ClientID', 'Status']
      })
      .firstPage();

    console.log('🔍 Tier 4 results:', clientRecords.length > 0 ? {
      found: true,
      count: clientRecords.length,
      matches: clientRecords.map(r => r.get('ClientID'))
    } : { found: false });

    if (clientRecords.length > 0) {
      const match = {
        id: clientRecords[0].id,
        name: clientRecords[0].get('Client Long Name'),
        clientId: clientRecords[0].get('ClientID'),
        isPartialMatch: true,
        matchType: 'partial-client-id',
        allMatches: clientRecords.map(r => ({
          id: r.id,
          name: r.get('Client Long Name'),
          clientId: r.get('ClientID')
        }))
      };
      console.log('🔍 Returning Tier 4 partial ClientID match:', match);
      return match;
    }

    console.log('🔍 No match found for:', clientName);
    return null;
  });
};

// Add new attendee and create attendance records for all sessions
export const addAttendee = async (soid, attendeeData) => {
  return trackApiCall('addAttendee', { soid, attendeeData: { ...attendeeData, email: '[masked]' } }, async () => {
    // First, get the Scheduled Offering to retrieve the Client
    // We need to query for the SO record using SOID formula instead of direct find
    const soRecords = await operationsBase(TABLES.SCHEDULED_OFFERINGS)
      .select({
        filterByFormula: `{SOID} = '${soid}'`,
        maxRecords: 1
      })
      .firstPage();

    if (!soRecords || soRecords.length === 0) {
      throw new Error(`Scheduled Offering with SOID ${soid} not found`);
    }

    const soRecord = soRecords[0];
    const soNumber = soRecord.get('SO');
    const soClientId = soRecord.get('Client') ? soRecord.get('Client')[0] : null;
    const soClientName = soRecord.get('Client Name');
    
    // Check if this is an open program
    const isOpenProgram = soClientName === 'OPEN-PROGRAM' || (Array.isArray(soClientName) && soClientName[0] === 'OPEN-PROGRAM');
    
    // Prepare attendee record fields
    const attendeeFields = {
      'First Name': attendeeData.firstName,
      'Last Name': attendeeData.lastName,
      'Email': attendeeData.email || '',
      'Notes': attendeeData.notes || '',
      'SO': soNumber, // Associate with the SO number
      'Status': 'Registered' // Set the Status field to "Registered"
    };

    // Handle client assignment based on program type
    let clientId = attendeeData.clientId;

    // If clientName is provided, try to look up the client ID
    if (!clientId && attendeeData.clientName) {
      try {
        const clientLookup = await lookupClientByName(attendeeData.clientName);
        if (clientLookup) {
          clientId = clientLookup.id;
          if (clientLookup.isPartialMatch) {
            console.warn(`Partial client match found: using best match`);
          }
        } else {
          console.warn(`Client not found in Airtable for attendee`);
        }
      } catch (error) {
        console.error(`Error looking up client:`, error.message);
      }
    }
    
    if (!clientId && !isOpenProgram) {
      // Regular program: use SO's client
      clientId = soClientId;
    } else if (!clientId && isOpenProgram) {
      // Open program without specific client: leave empty for manual assignment
      console.warn(`Open program detected. Attendee requires manual client assignment.`);
    }
    
    if (clientId) {
      attendeeFields['Client'] = [clientId];
    }

    // Idempotent create: two-tier dedup strategy to avoid creating duplicate attendees (#382).
    // Tier 1 (email present): match on Email + SO — most reliable, used when email is known.
    //   Scoped to the current SO because the same email on a different SO is a different enrollment.
    // Tier 2 (email empty, client known): match on First Name + Last Name + Client + SO.
    //   Client scoping prevents false matches between people with the same name at different companies.
    //   SO scoping keeps the lookup deterministic — without it, Airtable may return a same-name
    //   person from a different SO first, causing the current-SO match to be missed.
    // Tier 2 fallback (no client): match on First Name + Last Name + SO.
    const safeEmail = escapeAirtableString(attendeeData.email || '');
    const safeFirstName = escapeAirtableString(attendeeData.firstName || '');
    const safeLastName = escapeAirtableString(attendeeData.lastName || '');
    const safeClientId = escapeAirtableString(clientId || '');
    const existing = safeEmail
      ? await operationsBase(TABLES.ATTENDEES)
          .select({
            filterByFormula: `AND(LOWER({Email}) = LOWER("${safeEmail}"), {SO} = "${soNumber}")`,
            maxRecords: 1,
            fields: ['Email', 'Client', 'First Name', 'Last Name', 'Notes']
          })
          .firstPage()
      : safeClientId
        ? await operationsBase(TABLES.ATTENDEES)
            .select({
              filterByFormula: `AND(LOWER({First Name}) = LOWER("${safeFirstName}"), LOWER({Last Name}) = LOWER("${safeLastName}"), FIND("${safeClientId}", ARRAYJOIN({Client}, ",")), {SO} = "${soNumber}")`,
              maxRecords: 1,
              fields: ['Email', 'Client', 'First Name', 'Last Name', 'Notes']
            })
            .firstPage()
        : await operationsBase(TABLES.ATTENDEES)
            .select({
              filterByFormula: `AND(LOWER({First Name}) = LOWER("${safeFirstName}"), LOWER({Last Name}) = LOWER("${safeLastName}"), {SO} = "${soNumber}")`,
              maxRecords: 1,
              fields: ['Email', 'Client', 'First Name', 'Last Name', 'Notes']
            })
            .firstPage();
    const attendeeRecord = existing[0]
      ? existing[0]
      : await operationsBase(TABLES.ATTENDEES).create(attendeeFields);
    // If client resolved and record had no client, set it
    if (clientId && existing[0] && !(existing[0].get('Client') || []).length) {
      await operationsBase(TABLES.ATTENDEES).update(existing[0].id, { 'Client': [clientId] });
    }

    // Get all sessions for this SO
    const sessionRecords = await operationsBase(TABLES.SESSIONS)
      .select({
        filterByFormula: `{SO (from SOID)} = "${soNumber}"`,
        sort: [{ field: 'Session No', direction: 'asc' }]
      })
      .all();

    // Create attendance records in server-side batches (max 10 per request) — only missing ones
    const existingAttendance = await operationsBase(TABLES.SESSION_ATTENDANCE)
      .select({
        filterByFormula: `AND({Attendee} = "${attendeeRecord.id}", {SOID (from Session)} = "${soid}")`,
        fields: ['Session', 'Present', 'Notes']
      })
      .all();
    const existingSessionIds = new Set(
      existingAttendance.flatMap(r => r.get('Session') || [])
    );
    const payload = sessionRecords
      .filter(session => !existingSessionIds.has(session.id))
      .map(session => ({
        fields: {
          'Attendee': [attendeeRecord.id],
          'Session': [session.id],
          'Present': false
        }
      }));
    const attendanceRecords = [];
    for (let i = 0; i < payload.length; i += 10) {
      const batch = payload.slice(i, i + 10);
      try {
        const created = await operationsBase(TABLES.SESSION_ATTENDANCE).create(batch);
        attendanceRecords.push(...created);
        
        // Add small delay between batches to respect 5 req/sec limit
        if (i + 10 < payload.length) {
          await new Promise(resolve => setTimeout(resolve, 250));
        }
      } catch (error) {
        console.error(`Failed to create attendance records for batch starting at index ${i}:`, error);
        throw error;
      }
    }

    // Combine existing and newly created attendance records for complete session data
    const allAttendanceRecords = [...existingAttendance, ...attendanceRecords];
    
    // Get client information for display (using the clientId from attendee record)
    const attendeeClientRecordId = attendeeRecord.get('Client');
    const finalClientId = Array.isArray(attendeeClientRecordId) ? attendeeClientRecordId[0] : attendeeClientRecordId;

    // Get client name from attendee record's Company Name formula field
    // This matches what fetchAttendees returns and shows the short client ID (e.g., "BLG")
    const clientName = attendeeRecord.get('Company Name') || '';

    // Return formatted attendee data
    return {
      attendeeId: attendeeRecord.id,
      firstName: attendeeRecord.get('First Name'),
      lastName: attendeeRecord.get('Last Name'),
      email: attendeeRecord.get('Email') || '',
      notes: attendeeRecord.get('Notes') || '',
      clientId: finalClientId || null, // Include client record ID for UI display
      clientName: clientName, // Include client name for immediate display (e.g., "BLG")
      sessions: allAttendanceRecords.reduce((acc, record) => {
        const s = record.get('Session');
        const sessionId = Array.isArray(s) ? s[0] : s;
        acc[sessionId] = !!record.get('Present');
        return acc;
      }, {}),
      attendanceRecords: allAttendanceRecords.reduce((acc, record) => {
        const s = record.get('Session');
        const sessionId = Array.isArray(s) ? s[0] : s;
        acc[sessionId] = record.id;
        return acc;
      }, {}),
      sessionNotes: allAttendanceRecords.reduce((acc, record) => {
        const s = record.get('Session');
        const sessionId = Array.isArray(s) ? s[0] : s;
        acc[sessionId] = record.get('Notes') || '';
        return acc;
      }, {})
    };
  });
};

// Update attendee notes
export const updateAttendeeNotes = async (attendeeId, notes) => {
  return trackApiCall('updateAttendeeNotes', { attendeeId, notes: '[masked]' }, async () => {
    await operationsBase(TABLES.ATTENDEES).update(attendeeId, {
      'Notes': notes
    });
    return true;
  });
};

// Update session-specific notes in Session Attendance record
export const updateSessionNotes = async (attendanceRecordId, notes) => {
  return trackApiCall('updateSessionNotes', { attendanceRecordId, notes: '[masked]' }, async () => {
    
    // Check if record exists before updating
    try {
      await operationsBase(TABLES.SESSION_ATTENDANCE).find(attendanceRecordId);
    } catch (findError) {
      console.error('Error finding attendance record for notes:', findError);
      throw new Error(`Attendance record ${attendanceRecordId} not found`);
    }
    
    // Update the record with new notes
    await operationsBase(TABLES.SESSION_ATTENDANCE).update(attendanceRecordId, {
      'Notes': notes
    });
    
    return true;
  });
};

// Fetch session notes for a specific attendance record
export const fetchSessionNotes = async (attendanceRecordId) => {
  return trackApiCall('fetchSessionNotes', { attendanceRecordId }, async () => {
    
    const record = await operationsBase(TABLES.SESSION_ATTENDANCE).find(attendanceRecordId);
    const notes = record.get('Notes') || '';
    
    return notes;
  });
};

// Debug function to check if session attendance records exist
export const debugCheckSessionAttendance = async (sessionId) => {
  return trackApiCall('debugCheckSessionAttendance', { sessionId }, async () => {
    
    const records = await operationsBase(TABLES.SESSION_ATTENDANCE)
      .select({
        filterByFormula: `{Session} = '${sessionId}'`
      })
      .all();
      
    
    // Get details about each record
    const recordDetails = records.map(record => {
      return {
        id: record.id,
        attendee: record.get('Attendee'),
        session: record.get('Session'),
        present: record.get('Present'),
        createdTime: record.get('Created')
      };
    });
    
    
    return recordDetails;
  });
};

// Create a single attendance record
export const createAttendanceRecord = async (attendeeId, sessionId, present = true) => {
  return trackApiCall('createAttendanceRecord', { attendeeId, sessionId, present }, async () => {
    
    // Validate inputs
    if (!attendeeId || !sessionId) {
      throw new Error('Attendee ID and Session ID are required to create an attendance record');
    }
    
    // Create the attendance record with only the required fields
    const attendanceFields = {
      'Attendee': [attendeeId],
      'Session': [sessionId],
      'Present': present  // Use the provided present status
    };
    
    const newRecord = await operationsBase(TABLES.SESSION_ATTENDANCE).create(attendanceFields);
    
    
    // Return the created record
    return {
      id: newRecord.id,
      attendeeId: attendeeId,
      sessionId: sessionId,
      present: present
    };
  });
};

// Update SO notes in Scheduled Offerings table
export const updateSONotes = async (soid, notes) => {
  return trackApiCall('updateSONotes', { soid, notes: '[masked]' }, async () => {
    
    // Find the Scheduled Offering record by SOID
    const soRecords = await operationsBase(TABLES.SCHEDULED_OFFERINGS)
      .select({
        filterByFormula: `{SOID} = '${soid}'`,
        maxRecords: 1
      })
      .firstPage();

    if (!soRecords || soRecords.length === 0) {
      throw new Error(`Scheduled Offering with SOID ${soid} not found`);
    }

    const soRecord = soRecords[0];
    
    // Update the PL Notes field
    await operationsBase(TABLES.SCHEDULED_OFFERINGS).update(soRecord.id, {
      'PL Notes': notes
    });
    
    return true;
  });
};

// Fetch SO notes from Scheduled Offerings table
export const fetchSONotes = async (soid) => {
  return trackApiCall('fetchSONotes', { soid }, async () => {
    
    // Find the Scheduled Offering record by SOID
    const soRecords = await operationsBase(TABLES.SCHEDULED_OFFERINGS)
      .select({
        filterByFormula: `{SOID} = '${soid}'`,
        maxRecords: 1
      })
      .firstPage();

    if (!soRecords || soRecords.length === 0) {
      throw new Error(`Scheduled Offering with SOID ${soid} not found`);
    }

    const soRecord = soRecords[0];
    const notes = soRecord.get('PL Notes') || '';
    
    return notes;
  });
};

/**
 * Search for Scheduled Offerings using fuzzy matching.
 * Uses a cache-first strategy: checks sessionStorage preload cache (5-min TTL)
 * for matching results before falling back to an Airtable API query.
 * @param {string} query - Search query string for partial, case-insensitive matching against SO field
 * @returns {Promise<Array<{soNumber: string, programName: string, dates: string, status: string}>>}
 *   Array of matching SO objects, sorted by SO desc then Delivery Start desc. Empty array on error.
 */
export const searchScheduledOfferings = async (query) => {
  // Validate input
  if (!query || typeof query !== 'string' || query.trim().length === 0) {
    return [];
  }

  const trimmedQuery = query.trim();

  // Cache-first: check before trackApiCall to avoid rate limit delay on cache hits
  try {
    const cached = sessionStorage.getItem(PRELOAD_CACHE_KEYS.SO_SEARCH);
    if (cached) {
      const parsed = JSON.parse(cached);
      const ageMs = Date.now() - (parsed.timestamp || 0);
      const FIVE_MINUTES = 5 * 60 * 1000;
      if (ageMs < FIVE_MINUTES && Array.isArray(parsed.data)) {
        const lowerQuery = trimmedQuery.toLowerCase();
        const cacheResults = parsed.data
          .filter(record => {
            const so = (record.fields?.SO || '').toLowerCase();
            const status = record.fields?.['SO Status'] || '';
            return so.includes(lowerQuery) && status !== 'Cancelled';
          })
          .sort((a, b) => {
            const soCompare = (b.fields?.SO || '').localeCompare(a.fields?.SO || '');
            if (soCompare !== 0) return soCompare;
            const dateA = a.fields?.['Delivery Start'] ? new Date(a.fields['Delivery Start']).getTime() : 0;
            const dateB = b.fields?.['Delivery Start'] ? new Date(b.fields['Delivery Start']).getTime() : 0;
            return dateB - dateA;
          })
          .slice(0, 20)
          .map(record => {
            const deliveryStart = record.fields?.['Delivery Start'];
            const deliveryEnd = record.fields?.['Delivery End'];
            let dates = '';
            if (deliveryStart && deliveryEnd) {
              const startDate = new Date(deliveryStart).toLocaleDateString();
              const endDate = new Date(deliveryEnd).toLocaleDateString();
              dates = startDate === endDate ? startDate : `${startDate} - ${endDate}`;
            } else if (deliveryStart) {
              dates = new Date(deliveryStart).toLocaleDateString();
            }
            const programNameField = record.fields?.ProgramName;
            const programName = Array.isArray(programNameField) ? programNameField[0] || '' : programNameField || '';
            return {
              soNumber: record.fields?.SO || '',
              programName,
              dates,
              status: record.fields?.['SO Status'] || ''
            };
          });
        if (cacheResults.length > 0) {
          return cacheResults;
        }
      }
    }
  } catch (_cacheErr) {
    // Cache read failed — fall through to API
  }

  // API fallback via trackApiCall
  return trackApiCall('searchScheduledOfferings', { query }, async () => {
    try {
      // Use Airtable's SEARCH function with UPPER() for consistent case-insensitive matching
      const escapedQuery = escapeAirtableString(trimmedQuery);
      const soRecords = await operationsBase(TABLES.SCHEDULED_OFFERINGS)
        .select({
          filterByFormula: `AND(SEARCH(UPPER("${escapedQuery}"), UPPER({SO})) > 0, {SO Status} != "Cancelled")`,
          maxRecords: 20,
          sort: [
            { field: 'SO', direction: 'desc' }, // Most recent SOs first
            { field: 'Delivery Start', direction: 'desc' }
          ]
        })
        .all();


      // Transform records to the required format
      const results = soRecords.map(record => {
        const deliveryStart = record.get('Delivery Start');
        const deliveryEnd = record.get('Delivery End');
        
        // Format dates for display
        let dates = '';
        if (deliveryStart && deliveryEnd) {
          const startDate = new Date(deliveryStart).toLocaleDateString();
          const endDate = new Date(deliveryEnd).toLocaleDateString();
          dates = startDate === endDate ? startDate : `${startDate} - ${endDate}`;
        } else if (deliveryStart) {
          dates = new Date(deliveryStart).toLocaleDateString();
        }

        // Handle ProgramName which can be an array (lookup field)
        const programNameField = record.get('ProgramName');
        const programName = Array.isArray(programNameField) 
          ? programNameField[0] || '' 
          : programNameField || '';

        return {
          soNumber: record.get('SO') || '',
          programName: programName,
          dates: dates,
          status: record.get('SO Status') || ''
        };
      });

      return results;

    } catch (error) {
      console.error('Error searching Scheduled Offerings:', error);
      
      // Graceful fallback - return empty array instead of throwing
      // This ensures the search doesn't break the application
      Sentry.addBreadcrumb({
        message: 'Scheduled Offerings search failed, returning empty results',
        category: 'search-fallback',
        level: 'warning',
        data: { query: trimmedQuery, error: error.message }
      });
      
      return [];
    }
  });
};

export const getWeeklyScheduledOfferings = async () => {
  return trackApiCall('getWeeklyScheduledOfferings', {}, async () => {
    try {
      // Get from last Monday to next few weeks (extended range for better visibility)
      const today = new Date();
      const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
      
      // Calculate last Monday (or this Monday if today is Monday)
      const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
      const lastMonday = new Date(today);
      lastMonday.setDate(today.getDate() + mondayOffset - 7); // Go back one more week
      lastMonday.setHours(0, 0, 0, 0);
      
      // Calculate end date (3 weeks from last Monday)
      const endDate = new Date(lastMonday);
      endDate.setDate(lastMonday.getDate() + 20); // 3 weeks from last Monday
      endDate.setHours(23, 59, 59, 999);

      // Format dates for Airtable (YYYY-MM-DD format)
      const formatDate = (date) => date.toISOString().split('T')[0];
      const startDateStr = formatDate(lastMonday);
      const endDateStr = formatDate(endDate);

      // Query for scheduled offerings active in the time range
      const records = await operationsBase(TABLES.SCHEDULED_OFFERINGS)
        .select({
          filterByFormula: `AND(
            OR(
              {SO Status} = "Active",
              {SO Status} = "Confirmed",
              {SO Status} = "Completed"
            ),
            OR(
              AND(
                IS_AFTER({Delivery Start}, "${startDateStr}"),
                IS_BEFORE({Delivery Start}, "${endDateStr}")
              ),
              AND(
                IS_BEFORE({Delivery Start}, "${startDateStr}"),
                IS_AFTER({Delivery End}, "${startDateStr}")
              ),
              AND(
                IS_AFTER({Delivery End}, "${startDateStr}"),
                IS_BEFORE({Delivery End}, "${endDateStr}")
              )
            )
          )`,
          sort: [
            { field: 'Delivery Start', direction: 'asc' },
            { field: 'SO', direction: 'asc' }
          ]
        })
        .all();

      // Transform records to include necessary data for dashboard
      return records.map(record => {
        const deliveryStart = record.get('Delivery Start');
        const deliveryEnd = record.get('Delivery End');
        
        // Format date range for display
        const formatDisplayDate = (dateStr) => {
          if (!dateStr) return '';
          const date = new Date(dateStr);
          return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
          });
        };

        const dateRange = deliveryStart && deliveryEnd 
          ? `${formatDisplayDate(deliveryStart)} - ${formatDisplayDate(deliveryEnd)}`
          : deliveryStart 
          ? `Starting ${formatDisplayDate(deliveryStart)}`
          : 'Date TBA';

        const programName = record.get('ProgramName');
        const programDisplayName = Array.isArray(programName) ? programName[0] : (programName || 'Unknown Program');
        
        return {
          id: record.id,
          SOID: record.get('SO'),
          'Program Name': programDisplayName,
          'PL Name': record.get('PL Name') || 'No PL Assigned',
          Status: record.get('SO Status') || 'Unknown',
          'Date Range': dateRange,
          'Delivery Start': deliveryStart,
          'Delivery End': deliveryEnd
        };
      });
    } catch (error) {
      console.error('Error fetching weekly scheduled offerings:', error);
      // Return empty array on error to prevent dashboard from breaking
      return [];
    }
  });
};

/**
 * Staff caching helper - staff records rarely change so cache for 1 hour
 */
const STAFF_CACHE_KEY = 'admin_staff_cache';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

const getCachedStaff = async () => {
  const hasSessionStorage = typeof sessionStorage !== 'undefined';
  console.log('🔍 SessionStorage debug:', {
    available: hasSessionStorage,
    length: hasSessionStorage ? sessionStorage.length : 0,
    keys: hasSessionStorage ? Object.keys(sessionStorage) : [],
    cacheKey: STAFF_CACHE_KEY
  });
  if (!hasSessionStorage) {
    // No caching available; just fetch fresh data
    const staffRecords = await operationsBase('Staff')
      .select({ fields: ['Name', 'Color'], filterByFormula: `{Status} = "Active"` })
      .all();
    return staffRecords;
  }
  
  // Try to get from cache first
  try {
    const cached = sessionStorage.getItem(STAFF_CACHE_KEY);
    console.log('🔍 Cache lookup result:', cached ? `Found ${cached.length} chars` : 'NULL - no cache found');
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      
      // Validate cache data structure
      if (Array.isArray(data) && typeof timestamp === 'number') {
        if (Date.now() - timestamp < CACHE_TTL) {
          console.log(`Using cached staff data (${data.length} records)`);
          
          // Convert cached data back to record-like objects with .get() method
          const cachedRecords = data.map(item => {
            // Validate each cached item has required structure
            if (item && item.id && item.fields) {
              return {
                id: item.id,
                get: (fieldName) => item.fields[fieldName],
                fields: item.fields
              };
            } else {
              console.warn('Invalid cached staff item structure, falling back to fresh fetch');
              throw new Error('Invalid cache item structure');
            }
          });
          
          return cachedRecords;
        } else {
          console.log('Staff cache expired, fetching fresh data');
          // Clear expired cache
          sessionStorage.removeItem(STAFF_CACHE_KEY);
        }
      } else {
        console.warn('Invalid cache data structure, clearing cache and fetching fresh data');
        sessionStorage.removeItem(STAFF_CACHE_KEY);
      }
    } else {
      console.log('No staff cache found, fetching fresh data');
    }
  } catch (error) {
    console.warn('Error reading staff cache, falling back to fresh fetch:', error);
    // Clear potentially corrupted cache
    try {
      sessionStorage.removeItem(STAFF_CACHE_KEY);
    } catch (clearError) {
      console.warn('Could not clear corrupted cache:', clearError);
    }
  }

  // Cache miss or expired - fetch from Airtable
  console.time('staff-fetch');
  const staffRecords = await operationsBase('Staff')
    .select({
      fields: ['Name', 'Color'],
      filterByFormula: `{Status} = "Active"`
    })
    .all();
  console.timeEnd('staff-fetch');

  // Cache the results - store only the data we need, not the full Airtable records
  try {
    // Validate records before caching
    if (!Array.isArray(staffRecords) || staffRecords.length === 0) {
      console.warn('No staff records to cache');
      return staffRecords;
    }
    
    const serializedData = staffRecords.map(record => {
      // Validate each record has required methods and data
      if (!record || typeof record.get !== 'function' || !record.id) {
        console.warn('Invalid staff record structure, skipping cache');
        throw new Error('Invalid record structure');
      }
      
      return {
        id: record.id,
        fields: {
          'Name': record.get('Name'),
          'Color': record.get('Color')
        }
      };
    });
    
    const cacheData = {
      data: serializedData,
      timestamp: Date.now()
    };
    
    const cacheString = JSON.stringify(cacheData);
    
    // Check if storage quota exceeded
    if (cacheString.length > 1024 * 1024) { // 1MB limit
      console.warn('Staff cache data too large, skipping cache');
      return staffRecords;
    }
    
    sessionStorage.setItem(STAFF_CACHE_KEY, cacheString);
    console.log(`✅ Cached ${staffRecords.length} staff records`);
    
    // Debug: Immediate verification
    const immediateCheck = sessionStorage.getItem(STAFF_CACHE_KEY);
    console.log('🔍 Immediate cache verification:', immediateCheck ? 'SUCCESS' : 'FAILED');
    console.log('🔍 SessionStorage now has', sessionStorage.length, 'items');
    console.log('🔍 All sessionStorage keys:', Object.keys(sessionStorage));
    console.log('🔍 Cache data size:', immediateCheck ? `${immediateCheck.length} characters` : 'N/A');
  } catch (error) {
    console.warn('Error caching staff data, continuing without cache:', error);
    // Don't clear existing cache if storage fails - it might be quota issue
  }

  return staffRecords;
};

export const getWeeklySessions = async () => {
  return trackApiCall('getWeeklySessions', {}, async () => {
    try {
      // Check if we can use preloaded sessions data
      const preloadedSessions = getPreloadedSessions();
      const preloadedSOs = getPreloadedScheduledOfferings();
      
      // Get sessions from 7 days ago to end of next week
      const today = new Date();
      
      // Calculate 7 days ago
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);
      sevenDaysAgo.setHours(0, 0, 0, 0);
      
      // Calculate end of next week (next Sunday)
      const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const nextSunday = new Date(today);
      const daysUntilNextSunday = currentDay === 0 ? 7 : 7 - currentDay + 7; // Next Sunday, not this Sunday
      nextSunday.setDate(today.getDate() + daysUntilNextSunday);
      nextSunday.setHours(23, 59, 59, 999);

      // Format dates for Airtable (YYYY-MM-DD format)
      const formatDate = (date) => date.toISOString().split('T')[0];
      const startDateStr = formatDate(sevenDaysAgo);
      const endDateStr = formatDate(nextSunday);

      // PERFORMANCE OPTIMIZATION: Try to use preloaded data first, then fetch only what's missing
      console.time('getWeeklySessions-total');
      
      let sessionRecords = [];
      
      // Check if preloaded sessions cover our date range
      if (preloadedSessions && preloadedSessions.length > 0) {
        console.log('🚀 Attempting to use preloaded sessions data...');
        
        // Filter preloaded sessions to our specific date range
        const filteredPreloaded = preloadedSessions.filter(session => {
          const sessionDate = session.fields?.Start;
          if (!sessionDate) return false;
          
          const sessionDateStr = sessionDate.split('T')[0]; // Get YYYY-MM-DD
          return sessionDateStr >= startDateStr && sessionDateStr <= endDateStr;
        });
        
        if (filteredPreloaded.length > 0) {
          console.log(`💾 Using ${filteredPreloaded.length} preloaded sessions (${startDateStr} to ${endDateStr})`);
          // Convert back to Airtable-like records
          sessionRecords = filteredPreloaded.map(session => ({
            id: session.id,
            fields: session.fields,
            get: (fieldName) => session.fields[fieldName]
          }));
        }
      }
      
      // If no preloaded data or insufficient coverage, fetch from API
      if (sessionRecords.length === 0) {
        console.time('sessions-fetch');
        console.log('🔄 Fetching sessions from API (no preloaded data available)...');
        
        sessionRecords = await operationsBase(TABLES.SESSIONS)
          .select({
            // Simplified date filtering for better performance - use string comparison which can be faster
            filterByFormula: `AND(
              IS_AFTER({Start}, "${startDateStr}"),
              IS_BEFORE({Start}, "${endDateStr}")
            )`,
            sort: [
              { field: 'Start', direction: 'desc' } // Most recent first
            ],
            maxRecords: 50, // Reasonable limit for display
          fields: [
            'Session Name', 'Start', 'SOID', 'SO (from SOID)', 
            'ProgramName (from SOID)', 'PL', 'PL Override',
            'Session Status', 'Survey Link TRUTH'
          ] // Only fetch needed fields
          })
          .all();
        
        console.timeEnd('sessions-fetch');
        console.log(`Fetched ${sessionRecords.length} sessions for date range ${startDateStr} to ${endDateStr}`);
      } else {
        console.log(`✅ Using preloaded sessions data (${sessionRecords.length} sessions) - API fetch skipped!`);
      }
      
      // Performance warning for extremely slow responses
      const sessionsFetchTime = performance.now() - performance.mark('sessions-start')?.detail || 0;
      if (sessionsFetchTime > 10000) {
        console.warn(`🐌 VERY SLOW sessions fetch: ${sessionsFetchTime.toFixed(0)}ms - possible Airtable API slowdown`);
      }

      // Extract unique SO numbers and SOIDs from sessions to minimize API calls
      const uniqueSONumbers = [...new Set(sessionRecords
        .map(record => {
          const soNumber = record.get('SO (from SOID)');
          return Array.isArray(soNumber) ? soNumber[0] : soNumber;
        })
        .filter(Boolean)
      )];
      
      const uniqueSOIDs = [...new Set(sessionRecords
        .map(record => record.get('SOID'))
        .filter(Boolean)
        .flat()
      )];
      
      console.log(`Need data for ${uniqueSONumbers.length} unique SO numbers and ${uniqueSOIDs.length} unique SOIDs`);

      // Step 2: Fetch only the data we need in parallel (with preload optimization)
      console.time('optimized-parallel-fetch');
      
      // Try to use preloaded scheduled offerings first
      let scheduledOfferingsPromise;
      if (preloadedSOs && preloadedSOs.length > 0 && uniqueSONumbers.length > 0) {
        console.log('🚀 Checking preloaded scheduled offerings...');
        
        // Filter preloaded SOs to match our needed SO numbers
        const matchingPreloadedSOs = preloadedSOs.filter(so => 
          uniqueSONumbers.includes(so.fields?.SO)
        );
        
        if (matchingPreloadedSOs.length === uniqueSONumbers.length) {
          console.log(`💾 Using ${matchingPreloadedSOs.length} preloaded scheduled offerings - API fetch skipped!`);
          // Convert back to Airtable-like records
          scheduledOfferingsPromise = Promise.resolve(
            matchingPreloadedSOs.map(so => ({
              id: so.id,
              fields: so.fields,
              get: (fieldName) => so.fields[fieldName]
            }))
          );
        } else {
          console.log(`🔄 Partial preload match (${matchingPreloadedSOs.length}/${uniqueSONumbers.length}) - fetching from API`);
          scheduledOfferingsPromise = operationsBase(TABLES.SCHEDULED_OFFERINGS)
            .select({
              filterByFormula: uniqueSONumbers.length === 1 
                ? `{SO} = "${uniqueSONumbers[0]}"` 
                : `OR(${uniqueSONumbers.map(so => `{SO} = "${so}"`).join(',')})`,
              fields: ['SO', 'SOID', 'Client Name', 'Type', 'SO Status']
            })
            .all();
        }
      } else {
        console.log('🔄 No preloaded SOs available - fetching from API');
        scheduledOfferingsPromise = uniqueSONumbers.length > 0 
          ? operationsBase(TABLES.SCHEDULED_OFFERINGS)
              .select({
                filterByFormula: uniqueSONumbers.length === 1 
                  ? `{SO} = "${uniqueSONumbers[0]}"` 
                  : `OR(${uniqueSONumbers.map(so => `{SO} = "${so}"`).join(',')})`,
                fields: ['SO', 'SOID', 'Client Name', 'Type', 'SO Status']
              })
              .all()
          : Promise.resolve([]);
      }

      const [scheduledOfferingsRecords, staffRecords, attendanceRecords, registeredAttendeesRecords] = await Promise.all([
        scheduledOfferingsPromise,
        
        // Staff records with caching (rarely change, good candidate for caching)
        getCachedStaff(),
          
        // Only fetch attendance records for this week's sessions
        uniqueSOIDs.length > 0
          ? operationsBase(TABLES.SESSION_ATTENDANCE)
              .select({
                filterByFormula: uniqueSOIDs.length === 1
                  ? `{SOID (from Session)} = "${uniqueSOIDs[0]}"`
                  : `OR(${uniqueSOIDs.map(soid => `{SOID (from Session)} = "${soid}"`).join(',')})`,
                fields: ['Attendee', 'SOID (from Session)']
              })
              .all()
          : Promise.resolve([]),
          
        // Fetch registered attendees for each SO number
        uniqueSONumbers.length > 0
          ? operationsBase(TABLES.ATTENDEES)
              .select({
                filterByFormula: uniqueSONumbers.length === 1
                  ? `{SO} = "${uniqueSONumbers[0]}"`
                  : `OR(${uniqueSONumbers.map(so => `{SO} = "${so}"`).join(',')})`,
                fields: ['SO']
              })
              .all()
          : Promise.resolve([])
      ]);
      
      console.timeEnd('optimized-parallel-fetch');
      console.log(`Performance optimization results:
        - Scheduled Offerings: ${scheduledOfferingsRecords.length} records (instead of ALL)
        - Staff: ${staffRecords.length} records
        - Attendance: ${attendanceRecords.length} records (instead of up to 2000)
        - Registered Attendees: ${registeredAttendeesRecords.length} records`
      );

      // Fallback check: if we have sessions but no scheduled offerings, there might be a data issue
      if (sessionRecords.length > 0 && scheduledOfferingsRecords.length === 0) {
        console.warn('⚠️ Found sessions but no matching scheduled offerings. This may indicate a data inconsistency.');
        console.log('Session SO numbers:', uniqueSONumbers);
      }

      // Create a map of SO number to client info (more reliable than SOID matching)
      const clientInfoMap = scheduledOfferingsRecords.reduce((map, record) => {
        const soNumber = record.get('SO');
        if (soNumber) {
          map[soNumber] = {
            clientName: record.get('Client Name'),
            type: record.get('Type'),
            soStatus: record.get('SO Status')
          };
        }
        return map;
      }, {});

      // Create a map of staff ID to staff info (for facilitator colors)
      const staffInfoMap = staffRecords.reduce((map, record) => {
        const staffId = record.id;
        const staffName = record.get('Name');
        const staffColor = record.get('Color');
        
        if (staffId && staffName) {
          map[staffId] = {
            name: staffName,
            color: staffColor || 'gray' // Default to gray if no color specified
          };
        }
        return map;
      }, {});

      // Create a map of SO number to registered attendee count from Attendees table
      const registeredAttendeeCountMap = {};
      
      registeredAttendeesRecords.forEach(record => {
        const soNumber = record.get('SO');
        
        if (soNumber) {
          if (!registeredAttendeeCountMap[soNumber]) {
            registeredAttendeeCountMap[soNumber] = 0;
          }
          registeredAttendeeCountMap[soNumber]++;
        }
      });
      

      // Filter out sessions from cancelled SOs, then transform records
      const activeSessions = sessionRecords.filter(record => {
        const soNumber = record.get('SO (from SOID)');
        const soNumberDisplay = Array.isArray(soNumber) ? soNumber[0] : soNumber;
        const clientInfo = clientInfoMap[soNumberDisplay] || {};
        return clientInfo.soStatus !== 'Cancelled';
      });

      // Transform records to include necessary data for dashboard
      const result = activeSessions.map(record => {
        const start = record.get('Start');
        const sessionName = record.get('Session Name') || 'Session';
        // const sessionSoid = record.get('SOID');
        const soNumber = record.get('SO (from SOID)');
        const soNumberDisplay = Array.isArray(soNumber) ? soNumber[0] : soNumber;
        const programName = record.get('ProgramName (from SOID)');
        const programDisplayName = Array.isArray(programName) ? programName[0] : (programName || 'Unknown Program');
        
        // Get facilitator info with PL Override taking precedence
        // PL Override allows session-specific facilitator assignment that overrides the SO-level PL
        // This handles cases where different sessions within an SO have different facilitators
        const plOverrideField = record.get('PL Override');
        const plField = record.get('PL');
        
        // Use PL Override if set, otherwise fall back to regular PL field
        let facilitatorIds;
        if (plOverrideField) {
          facilitatorIds = Array.isArray(plOverrideField) ? plOverrideField : [plOverrideField];
        } else {
          facilitatorIds = Array.isArray(plField) ? plField : (plField ? [plField] : []);
        }
        
        const facilitators = facilitatorIds.map(id => staffInfoMap[id]).filter(Boolean);
        
        // For backward compatibility, keep single facilitator fields (first PL)
        const primaryFacilitator = facilitators[0] || null;
        
        // Get client info from the map using SO number (more reliable than SOID matching)
        const clientInfo = clientInfoMap[soNumberDisplay] || {};
        const clientName = clientInfo.clientName;
        const clientDisplayName = Array.isArray(clientName) ? clientName[0] : clientName;
        const programType = clientInfo.type;
        const soStatus = clientInfo.soStatus;
        
        
        // Extract program code from Survey Link (e.g., "ProgramID=CL" -> "CL")
        // Handle potential array values from Airtable lookup fields
        let surveyLinkValue = record.get('Survey Link TRUTH');
        if (Array.isArray(surveyLinkValue)) {
          surveyLinkValue = surveyLinkValue[0];
        }
        const surveyLink = surveyLinkValue != null ? String(surveyLinkValue) : '';
        
        // Only run regex match if we have a valid non-empty string
        let programCode = null;
        if (typeof surveyLink === 'string' && surveyLink.length > 0) {
          const programCodeMatch = surveyLink.match(/ProgramID=([^&]+)/);
          programCode = programCodeMatch ? programCodeMatch[1] : null;
        }
        
        // Format date and time for display
        const formatDateTime = (dateTimeStr) => {
          if (!dateTimeStr) return 'Date TBA';
          const date = new Date(dateTimeStr);
          return date.toLocaleDateString('en-US', { 
            weekday: 'short',
            month: 'short', 
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
        };

        // Determine session timing relative to today
        const now = new Date();
        const sessionDate = start ? new Date(start) : null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);
        
        const isToday = sessionDate && sessionDate >= today && sessionDate <= todayEnd;
        const isPast = sessionDate && sessionDate < now;
        const isUpcoming = sessionDate && sessionDate > now;

        // Determine client prefix based on program Type - if it's exactly "Open/Trial", show "Open Program", otherwise show client name
        const isOpenProgram = programType === 'Open/Trial';
        const clientPrefix = isOpenProgram ? 'Open Program' : (clientDisplayName || 'Unknown Client');

        // Format session display name with client and program code
        const displaySessionName = programCode 
          ? `${clientPrefix} - ${programCode}: ${sessionName}`
          : `${clientPrefix}: ${sessionName}`;

        return {
          id: record.id,
          sessionName: displaySessionName,
          soNumber: soNumberDisplay,
          programName: programDisplayName,
          programCode: programCode,
          clientName: clientDisplayName,
          clientPrefix: clientPrefix,
          soStatus: soStatus,
          programType: programType,
          isOpenProgram: isOpenProgram,
          dateTime: formatDateTime(start),
          rawStart: start,
          isPast: isPast,
          isToday: isToday,
          isUpcoming: isUpcoming,
          status: record.get('Session Status') || 'Scheduled',
          // Multiple facilitators support
          facilitators: facilitators,
          facilitatorIds: facilitatorIds,
          
          // Backward compatibility fields (primary facilitator)
          facilitatorId: facilitatorIds[0] || null,
          facilitatorName: primaryFacilitator?.name || 'Unassigned',
          facilitatorColor: primaryFacilitator?.color || 'gray',
          expectedAttendees: registeredAttendeeCountMap[soNumberDisplay] || 0
        };
      });
      
      // Debug: Log first session to verify expectedAttendees is included
      console.log('First session with attendee count:', result[0] ? {
        sessionName: result[0].sessionName,
        expectedAttendees: result[0].expectedAttendees
      } : 'No sessions');
      
      console.timeEnd('getWeeklySessions-total');
      console.log(`getWeeklySessions completed: ${result.length} sessions processed`);
      
      return result;
    } catch (error) {
      console.error('Error fetching weekly sessions:', error);
      // Return empty array on error to prevent dashboard from breaking
      return [];
    }
  });
};

/**
 * Centralized helper for counting sessions in a date range
 * Wraps operationsBase calls with proper tracking and rate limiting
 */
export const countSessionsInDateRange = async (startDateStr, endDateStr) => {
  return await trackApiCall('countSessionsInDateRange', { startDateStr, endDateStr }, async () => {
    try {
      const sessions = await operationsBase('Sessions')
        .select({
          filterByFormula: `AND(
            NOT(IS_BEFORE({Start}, DATETIME_PARSE("${startDateStr}", "YYYY-MM-DD"))),
            IS_BEFORE({Start}, DATETIME_PARSE("${endDateStr}", "YYYY-MM-DD"))
          )`,
          fields: ['Session No'], // Only fetch needed field
          maxRecords: 1000  // Performance protection
        })
        .all();
      
      return sessions.length;
    } catch (error) {
      console.error('Error counting sessions in date range:', error);
      return 0;
    }
  });
};

/**
 * Centralized helper for counting sessions on a specific date
 * Wraps operationsBase calls with proper tracking and rate limiting
 */
export const countSessionsOnDate = async (dateStr) => {
  return await trackApiCall('countSessionsOnDate', { dateStr }, async () => {
    try {
      const sessions = await operationsBase('Sessions')
        .select({
          filterByFormula: `IS_SAME({Start}, DATETIME_PARSE("${dateStr}", "YYYY-MM-DD"), "day")`,
          fields: ['Session No'], // Only fetch needed field
          maxRecords: 1000  // Performance protection
        })
        .all();
      
      return sessions.length;
    } catch (error) {
      console.error('Error counting sessions on date:', error);
      return 0;
    }
  });
};

/**
 * Get recently created attendees for admin dashboard
 * @param {number} days - Number of days back to look
 * @param {number} maxRecords - Maximum records to return
 * @returns {Promise<Array>} Array of Airtable records
 */
export const getRecentAttendeesCreated = async (days = 7, maxRecords = 5) => {
  return await trackApiCall('getRecentAttendeesCreated', { days, maxRecords }, async () => {
    try {
      const records = await operationsBase(TABLES.ATTENDEES)
        .select({
          filterByFormula: `IS_AFTER(CREATED_TIME(), DATEADD(TODAY(), -${days}, 'days'))`,
          sort: [{ field: 'CreatedTime', direction: 'desc' }],
          maxRecords,
          fields: ['Full Name', 'SO']
        })
        .all();
      return records;
    } catch (error) {
      console.error('Error fetching recent attendees:', error);
      return [];
    }
  });
};

/**
 * Get recently created scheduled offerings for admin dashboard
 * @param {number} days - Number of days back to look
 * @param {number} maxRecords - Maximum records to return
 * @returns {Promise<Array>} Array of Airtable records
 */
export const getRecentScheduledOfferingsCreated = async (days = 30, maxRecords = 3) => {
  return await trackApiCall('getRecentScheduledOfferingsCreated', { days, maxRecords, table: TABLES.SCHEDULED_OFFERINGS }, async () => {
    try {
      const records = await operationsBase(TABLES.SCHEDULED_OFFERINGS)
        .select({
          filterByFormula: `IS_AFTER(CREATED_TIME(), DATEADD(TODAY(), -${days}, 'days'))`,
          sort: [{ field: 'CreatedTime', direction: 'desc' }],
          maxRecords,
          fields: ['SO', 'ProgramName', 'SO Status']
        })
        .all();
      return records;
    } catch (error) {
      console.error('Error fetching recent scheduled offerings:', error);
      return [];
    }
  });
};

// Fetch sessions for any date range (for calendar dynamic loading)
export const fetchSessionsForDateRange = async (startDate, endDate) => {
  return trackApiCall('fetchSessionsForDateRange', { startDate, endDate }, async () => {
    try {
      // Format dates for Airtable (YYYY-MM-DD format)
      const formatDate = (date) => date.toISOString().split('T')[0];
      const startDateStr = formatDate(startDate);
      const endDateStr = formatDate(endDate);

      console.log(`🗓️ Fetching sessions for date range: ${startDateStr} to ${endDateStr}`);
      
      // Check if preloaded sessions cover this date range
      const preloadedSessions = getPreloadedSessions();
      if (preloadedSessions && preloadedSessions.length > 0) {
        // Filter preloaded sessions to the requested date range
        const filteredPreloaded = preloadedSessions.filter(session => {
          const sessionDate = session.fields?.Start;
          if (!sessionDate) return false;
          
          const sessionDateStr = sessionDate.split('T')[0]; // Get YYYY-MM-DD
          return sessionDateStr >= startDateStr && sessionDateStr <= endDateStr;
        });
        
        if (filteredPreloaded.length > 0) {
          console.log(`💾 Using ${filteredPreloaded.length} preloaded sessions for date range`);
          // Convert back to Airtable-like records
          const sessionRecords = filteredPreloaded.map(session => ({
            id: session.id,
            fields: session.fields,
            get: (fieldName) => session.fields[fieldName]
          }));
          
          // Still need to fetch related data (SOs, staff) for these sessions
          return await enrichSessionsWithRelatedData(sessionRecords);
        }
      }
      
      // Fetch from API if not in preload cache
      console.log('🔄 Fetching sessions from API for date range');
      const sessionRecords = await operationsBase(TABLES.SESSIONS)
        .select({
          filterByFormula: `AND(
            IS_AFTER({Start}, "${startDateStr}"),
            IS_BEFORE({Start}, "${endDateStr}")
          )`,
          sort: [{ field: 'Start', direction: 'desc' }],
          maxRecords: 200, // Higher limit for calendar view
          fields: [
            'Session Name', 'Start', 'SOID', 'SO (from SOID)', 
            'ProgramName (from SOID)', 'PL', 'PL Override',
            'Session Status', 'Survey Link TRUTH'
          ]
        })
        .all();
      
      console.log(`📅 Fetched ${sessionRecords.length} sessions for ${startDateStr} to ${endDateStr}`);
      
      // Enrich with related data
      return await enrichSessionsWithRelatedData(sessionRecords);
    } catch (error) {
      console.error('Error fetching sessions for date range:', error);
      throw error;
    }
  });
};

// Helper function to enrich sessions with related SO and staff data
const enrichSessionsWithRelatedData = async (sessionRecords) => {
  if (!sessionRecords || sessionRecords.length === 0) return [];
  
  // Extract unique identifiers
  const uniqueSONumbers = [...new Set(sessionRecords
    .map(record => {
      const soNumber = record.get('SO (from SOID)');
      return Array.isArray(soNumber) ? soNumber[0] : soNumber;
    })
    .filter(Boolean)
  )];
  
  // const uniqueSOIDs = [...new Set(sessionRecords
  //   .map(record => record.get('SOID'))
  //   .filter(Boolean)
  //   .flat()
  // )];
  
  // Fetch related data in parallel
  const [scheduledOfferingsRecords, staffRecords] = await Promise.all([
    // Try to use preloaded SOs first
    (async () => {
      const preloadedSOs = getPreloadedScheduledOfferings();
      if (preloadedSOs && uniqueSONumbers.length > 0) {
        const matchingPreloadedSOs = preloadedSOs.filter(so => 
          uniqueSONumbers.includes(so.fields?.SO)
        );
        
        if (matchingPreloadedSOs.length === uniqueSONumbers.length) {
          console.log(`💾 Using ${matchingPreloadedSOs.length} preloaded SOs for calendar`);
          return matchingPreloadedSOs.map(so => ({
            id: so.id,
            fields: so.fields,
            get: (fieldName) => so.fields[fieldName]
          }));
        }
      }
      
      // Fetch from API if not fully covered by preload
      if (uniqueSONumbers.length === 0) return [];
      
      return await operationsBase(TABLES.SCHEDULED_OFFERINGS)
        .select({
          filterByFormula: uniqueSONumbers.length === 1 
            ? `{SO} = "${uniqueSONumbers[0]}"` 
            : `OR(${uniqueSONumbers.map(so => `{SO} = "${so}"`).join(',')})`,
          fields: ['SO', 'SOID', 'Client Name', 'Type', 'SO Status']
        })
        .all();
    })(),
    
    // Use cached staff data
    getCachedStaff()
  ]);
  
  // Process and return enriched session data (simplified for calendar)
  // Create client info map
  const clientInfoMap = scheduledOfferingsRecords.reduce((map, record) => {
    const soNumber = record.get('SO');
    if (soNumber) {
      map[soNumber] = {
        clientName: record.get('Client Name'),
        soStatus: record.get('SO Status'),
        type: record.get('Type')
      };
    }
    return map;
  }, {});

  // Create staff info map
  const staffInfoMap = staffRecords.reduce((map, record) => {
    const staffId = record.id;
    const staffName = record.get('Name');
    const staffColor = record.get('Color');
    
    if (staffId && staffName) {
      map[staffId] = {
        name: staffName,
        color: staffColor || 'gray'
      };
    }
    return map;
  }, {});

  // Filter out sessions from cancelled SOs and transform records
  const activeSessions = sessionRecords.filter(record => {
    const soNumber = record.get('SO (from SOID)');
    const soNumberDisplay = Array.isArray(soNumber) ? soNumber[0] : soNumber;
    const clientInfo = clientInfoMap[soNumberDisplay] || {};
    return clientInfo.soStatus !== 'Cancelled';
  });

  return activeSessions.map(record => {
    const sessionStart = record.get('Start');
    const soNumber = record.get('SO (from SOID)');
    const soNumberDisplay = Array.isArray(soNumber) ? soNumber[0] : soNumber;
    const sessionName = record.get('Session Name');
    const programName = record.get('ProgramName (from SOID)');
    const facilitatorId = record.get('PL');
    const facilitatorOverride = record.get('PL Override');
    
    // Use override facilitator if available, otherwise use regular PL
    const actualFacilitatorId = facilitatorOverride && facilitatorOverride.length > 0 
      ? facilitatorOverride[0] 
      : (facilitatorId && facilitatorId.length > 0 ? facilitatorId[0] : null);
    
    const facilitatorInfo = actualFacilitatorId ? staffInfoMap[actualFacilitatorId] : null;
    const clientInfo = clientInfoMap[soNumberDisplay] || {};
    
    const sessionDate = new Date(sessionStart);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sessionDateOnly = new Date(sessionDate);
    sessionDateOnly.setHours(0, 0, 0, 0);
    
    return {
      id: record.id,
      sessionName: sessionName || 'Unnamed Session',
      soNumber: soNumberDisplay,
      programName: Array.isArray(programName) ? programName[0] : programName,
      facilitatorId: actualFacilitatorId,
      facilitatorName: facilitatorInfo?.name || 'Unassigned',
      facilitatorColor: facilitatorInfo?.color || 'gray',
      facilitators: facilitatorInfo ? [{ 
        id: actualFacilitatorId, 
        name: facilitatorInfo.name, 
        color: facilitatorInfo.color 
      }] : [],
      clientName: clientInfo.clientName || 'Unknown Client',
      type: clientInfo.type || 'Unknown',
      dateTime: sessionDate.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }),
      startTime: sessionDate,
      rawStart: sessionStart, // Calendar expects this field name
      expectedAttendees: 0, // For calendar, we don't need accurate count
      isToday: sessionDateOnly.getTime() === today.getTime(),
      isUpcoming: sessionDateOnly > today,
      isPast: sessionDateOnly < today
    };
  });
};

/**
 * Fetch all active clients with their previous/upcoming scheduled offerings and current training programs
 * Used by AdminClients component for client activity overview
 * @returns {Promise<Object>} Object containing clients array and stats
 */
export const getActiveClientsWithSOsAndPrograms = async () => {
  return trackApiCall('getActiveClientsWithSOsAndPrograms', {}, async () => {
    try {
      // Step 1: Fetch all active clients
      const activeClientsRecords = await operationsBase(TABLES.CLIENTS)
        .select({
          filterByFormula: `{Status} = "Active"`,
          fields: ['Client Long Name', 'Status', 'portal_token', 'portal_enabled'],
          sort: [{ field: 'Client Long Name', direction: 'asc' }]
        })
        .all();

      if (activeClientsRecords.length === 0) {
        return {
          clients: [],
          stats: {
            totalClients: 0,
            activePrograms: 0,
            upcomingSOs: 0,
            completionRate: "0%"
          }
        };
      }


      // Extract client IDs for batch queries
      // Extract client IDs for batch queries (currently unused but kept for future optimization)
      // const clientIds = activeClientsRecords.map(record => record.id);
      
      // Step 2: Fetch all scheduled offerings for these clients
      const today = new Date();

      // Fetch all non-cancelled SOs and filter by client on the client-side
      // This is more reliable than the complex FIND/ARRAYJOIN formula with linked records
      const scheduledOfferingsRecords = await operationsBase(TABLES.SCHEDULED_OFFERINGS)
        .select({
          filterByFormula: `NOT({SO Status} = "Cancelled")`,
          fields: [
            'SO', 'SOID', 'Client', 'Client Name', 'Participating Clients', 'ProgramName', 
            'Delivery Start', 'Delivery End', 'SO Status'
          ],
          sort: [
            { field: 'Delivery Start', direction: 'desc' }
          ]
        })
        .all();



      // Step 3: Process data for each client
      const clientsWithData = [];
      let totalActivePrograms = 0;
      let totalUpcomingSOs = 0;
      let completedSOs = 0;

      for (const clientRecord of activeClientsRecords) {
        const clientId = clientRecord.id;
        const clientName = clientRecord.get('Client Long Name') || 'Unknown Client';

        // Filter SOs for this client using the unified Participating Clients field
        const clientSOs = scheduledOfferingsRecords.filter(so => {
          // Try Participating Clients first (new unified field), fallback to Client (legacy)
          const participatingClientIds = so.get('Participating Clients') || [];
          const legacyClientIds = so.get('Client') || [];
          
          // Check both fields to handle migration period
          const allClientIds = [...participatingClientIds, ...legacyClientIds];
          
          return Array.isArray(allClientIds) && allClientIds.includes(clientId);
        });



        // Separate past and future SOs
        const pastSOs = clientSOs.filter(so => {
          const deliveryEnd = so.get('Delivery End');
          if (!deliveryEnd) return false;
          return new Date(deliveryEnd) < today;
        });

        const upcomingSOs = clientSOs.filter(so => {
          const deliveryStart = so.get('Delivery Start');
          if (!deliveryStart) return false;
          return new Date(deliveryStart) > today;
        });

        const currentPrograms = clientSOs.filter(so => {
          const deliveryStart = so.get('Delivery Start');
          const deliveryEnd = so.get('Delivery End');
          // const soStatus = so.get('SO Status'); // Currently unused but may be needed for future filtering
          
          if (!deliveryStart || !deliveryEnd) return false;
          
          const startDate = new Date(deliveryStart);
          const endDate = new Date(deliveryEnd);
          
          // Consider as current training program ONLY if currently running (between start and end dates)
          return startDate <= today && endDate >= today;
        });

        // Get most recent previous SO (sort by Delivery End desc to get truly most recent)
        const mostRecentPastSO = pastSOs.length > 0 
          ? pastSOs.sort((a, b) => new Date(b.get('Delivery End')) - new Date(a.get('Delivery End')))[0]
          : null;
        
        // Get next upcoming SO
        const nextUpcomingSO = upcomingSOs
          .sort((a, b) => new Date(a.get('Delivery Start')) - new Date(b.get('Delivery Start')))[0] || null;

        // Get current training program (most relevant active/confirmed SO)
        const currentProgram = currentPrograms
          .sort((a, b) => {
            // Prioritize currently running over future confirmed
            const aStart = new Date(a.get('Delivery Start'));
            const bStart = new Date(b.get('Delivery Start'));
            const aIsRunning = aStart <= today;
            const bIsRunning = bStart <= today;
            
            if (aIsRunning && !bIsRunning) return -1;
            if (!aIsRunning && bIsRunning) return 1;
            return aStart - bStart; // Earlier start date first
          })[0] || null;

        // Format SO data helper function
        const formatSOData = (soRecord) => {
          if (!soRecord) return null;
          
          const programName = soRecord.get('ProgramName');
          const programDisplayName = Array.isArray(programName) ? programName[0] : programName;
          const soStatus = soRecord.get('SO Status');
          const deliveryStart = soRecord.get('Delivery Start');
          const deliveryEnd = soRecord.get('Delivery End');
          
          return {
            id: soRecord.id,
            soNumber: soRecord.get('SO'),
            date: deliveryStart,
            programName: programDisplayName || 'Unknown Program',
            status: soStatus || 'Unknown',
            startDate: deliveryStart,
            endDate: deliveryEnd
          };
        };

        // Format training program data (similar to SO but with status)
        const formatProgramData = (soRecord) => {
          if (!soRecord) return null;
          
          const programName = soRecord.get('ProgramName');
          const programDisplayName = Array.isArray(programName) ? programName[0] : programName;
          const soStatus = soRecord.get('SO Status');
          const deliveryStart = soRecord.get('Delivery Start');
          const deliveryEnd = soRecord.get('Delivery End');
          
          return {
            id: soRecord.id,
            soNumber: soRecord.get('SO'),
            name: programDisplayName || 'Unknown Training Program',
            status: soStatus || 'Unknown',
            startDate: deliveryStart,
            endDate: deliveryEnd
          };
        };

        // Count stats
        if (currentProgram) totalActivePrograms++;
        if (nextUpcomingSO) totalUpcomingSOs++;
        // Count completed past SOs for this client (not just clients with any completed)
        completedSOs += pastSOs.filter(so => so.get('SO Status') === 'Completed').length;

        clientsWithData.push({
          id: clientId,
          name: clientName,
          previousSO: formatSOData(mostRecentPastSO),
          upcomingSO: formatSOData(nextUpcomingSO),
          currentProgram: formatProgramData(currentProgram),
          portal_token: clientRecord.get('portal_token'),
          portal_enabled: clientRecord.get('portal_enabled')
        });
      }

      // Calculate completion rate (completed SOs vs total past SOs)
      const totalPastSOs = scheduledOfferingsRecords.filter(so => {
        const deliveryEnd = so.get('Delivery End');
        return deliveryEnd && new Date(deliveryEnd) < today;
      }).length;
      
      const completionRate = totalPastSOs > 0 
        ? `${Math.round((completedSOs / totalPastSOs) * 100)}%`
        : "0%";

      const result = {
        clients: clientsWithData,
        stats: {
          totalClients: activeClientsRecords.length,
          activePrograms: totalActivePrograms,
          upcomingSOs: totalUpcomingSOs,
          completionRate
        }
      };

      
      return result;

    } catch (error) {
      console.error('Error fetching active clients with SOs and training programs:', error);
      
      Sentry.withScope((scope) => {
        scope.setTag('apiOperation', 'getActiveClientsWithSOsAndPrograms');
        scope.setContext('errorDetails', {
          message: error.message,
          stack: error.stack
        });
        Sentry.captureException(error);
      });
      
      // Return empty data structure on error to prevent component crashes
      return {
        clients: [],
        stats: {
          totalClients: 0,
          activePrograms: 0,
          upcomingSOs: 0,
          completionRate: "0%"
        }
      };
    }
  });
};

/**
 * Sample FAQ data for development/testing purposes
 * Returns FAQ data that demonstrates both general and program-specific FAQs
 */
const generateTestFAQData = (clientPrograms = []) => {
  const clientProgramNames = clientPrograms
    .map(program => program.programName)
    .filter(Boolean)
    .flat()
    .filter((name, index, array) => array.indexOf(name) === index);

  console.log('🧪 FAQ Test Mode Active - Generating sample FAQ data for programs:', clientProgramNames);

  const testFAQs = [
    // General FAQs (shown to all clients)
    {
      id: 'test-general-1',
      question: 'How do I access my training materials?',
      answer: 'You can access all your training materials through the client dashboard. Materials are available 24/7 and include session recordings, handouts, and additional resources.',
      category: 'General',
      programIds: [],
      displayOrder: 1
    },
    {
      id: 'test-general-2',
      question: 'What should I do if I miss a session?',
      answer: 'If you miss a session, don\'t worry! All sessions are recorded and available in your dashboard within 24 hours. You can also contact your Program Leader for a brief catch-up.',
      category: 'General',
      programIds: [],
      displayOrder: 2
    },
    {
      id: 'test-general-3',
      question: 'How do I contact my Program Leader?',
      answer: 'You can reach your Program Leader through the contact information provided in your program details, or use the message feature in the client dashboard.',
      category: 'General',
      programIds: [],
      displayOrder: 3
    },
    
    // Technical Support FAQs
    {
      id: 'test-tech-1',
      question: 'I\'m having trouble with video calls. What should I do?',
      answer: 'First, check your internet connection and ensure your browser is up to date. If issues persist, try joining from a different device or contact technical support at tech@avenue.ca.',
      category: 'Technical Support',
      programIds: [],
      displayOrder: 1
    },
    {
      id: 'test-tech-2',
      question: 'Can I download session recordings?',
      answer: 'Session recordings can be viewed online through the dashboard but are not available for download due to privacy and security reasons.',
      category: 'Technical Support',
      programIds: [],
      displayOrder: 2
    },
    
    // Connected Leader Program FAQs (program-specific)
    {
      id: 'test-cl-1',
      question: 'What is the Connected Leader assessment?',
      answer: 'The Connected Leader assessment is a comprehensive 360-degree feedback tool that measures your leadership effectiveness across key competencies. You\'ll receive detailed insights and development recommendations.',
      category: 'Program Content',
      programIds: ['connected-leader-id'],
      displayOrder: 1
    },
    {
      id: 'test-cl-2',
      question: 'When will I receive my leadership development plan?',
      answer: 'Your personalized leadership development plan will be available after Session 2, following your assessment completion and initial coaching discussion.',
      category: 'Program Content',
      programIds: ['connected-leader-id'],
      displayOrder: 2
    },
    
    // Startup Culture Builder FAQs (program-specific)
    {
      id: 'test-scb-1',
      question: 'What tools will I learn to build company culture?',
      answer: 'You\'ll learn practical tools including culture assessment frameworks, value-setting workshops, communication strategies, and measurement techniques for culture change.',
      category: 'Program Content',
      programIds: ['startup-culture-builder-id'],
      displayOrder: 1
    },
    {
      id: 'test-scb-2',
      question: 'How do I implement culture changes in my team?',
      answer: 'The program includes step-by-step implementation guides, templates, and ongoing coaching support to help you roll out culture initiatives effectively in your organization.',
      category: 'Program Content',
      programIds: ['startup-culture-builder-id'],
      displayOrder: 2
    }
  ];

  // Filter FAQs based on client programs (simulate real filtering logic)
  const relevantFAQs = testFAQs.filter(faq => {
    // Show general FAQs (no program assignment) to all clients
    if (!faq.programIds || faq.programIds.length === 0) {
      return true;
    }
    
    // Show program-specific FAQs only if client has that program
    // For test mode, check if any client program names match our test program patterns
    return faq.programIds.some(testProgramId => {
      if (testProgramId === 'connected-leader-id') {
        return clientProgramNames.some(name => 
          name && name.toLowerCase().includes('connected leader')
        );
      }
      if (testProgramId === 'startup-culture-builder-id') {
        return clientProgramNames.some(name => 
          name && (name.toLowerCase().includes('startup') || name.toLowerCase().includes('culture'))
        );
      }
      return false;
    });
  });

  // Group by category
  const faqsByCategory = relevantFAQs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {});

  console.log('🧪 Generated test FAQ data:', {
    totalFAQs: relevantFAQs.length,
    categories: Object.keys(faqsByCategory),
    clientPrograms: clientProgramNames
  });

  return faqsByCategory;
};

/**
 * Fetch FAQs filtered by client program associations
 * @param {string} clientId - Client record ID
 * @param {Array} clientPrograms - Array of client program objects with programName field
 * @returns {Promise<Object>} Object containing FAQs grouped by category
 */
export const fetchFAQsForClient = async (clientId, clientPrograms = []) => {
  return trackApiCall('fetchFAQsForClient', { clientId, programCount: clientPrograms.length }, async () => {
    try {
      // Development test mode - return sample data without hitting Airtable
      const isDev = (typeof import.meta !== 'undefined' && import.meta.env?.DEV) || process.env.NODE_ENV === 'development';
      const isTestMode = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FAQ_TEST_MODE === 'true') || process.env.VITE_FAQ_TEST_MODE === 'true';
      
      if (isDev && isTestMode) {
        console.log('🧪 FAQ Test Mode: Using sample FAQ data for development testing');
        return generateTestFAQData(clientPrograms);
      }

      // Step 1: Get Program record IDs that the client has access to
      // Extract program names from client programs
      const clientProgramNames = clientPrograms
        .map(program => program.programName)
        .filter(Boolean)
        .flat() // Handle case where programName might be an array
        .filter((name, index, array) => array.indexOf(name) === index); // Remove duplicates
      
      console.log(`Fetching FAQs for client ${clientId} with ${clientProgramNames.length} programs:`, clientProgramNames);
      
      // Step 2: Fetch Program record IDs from Airtable based on program names
      let programRecordIds = [];
      if (clientProgramNames.length > 0) {
        const formula = clientProgramNames.length === 1 
          ? `{Full Program Name} = "${escapeAirtableString(clientProgramNames[0])}"` 
          : `OR(${clientProgramNames.map(name => `{Full Program Name} = "${escapeAirtableString(name)}"`).join(', ')})`;
        
        
        const programRecords = await operationsBase(TABLES.PROGRAMS)
          .select({
            filterByFormula: formula,
            fields: ['Full Program Name', 'ProgramID']
          })
          .all();
        
        programRecordIds = programRecords.map(record => record.id);
        console.log(`Found ${programRecordIds.length} program record IDs for client programs`);
      }
      
      // Step 3: Fetch all published FAQs
      // Build a formula that includes both general FAQs (no program) and program-specific FAQs for this client
      let faqFilterFormula = `{Status} = "Published"`;
      
      if (programRecordIds.length > 0) {
        // Include FAQs with no program assignment OR FAQs linked to client's programs
        const programConditions = programRecordIds.map(id => `FIND("${id}", ARRAYJOIN({Program})) > 0`).join(', ');
        faqFilterFormula = `AND({Status} = "Published", OR(LEN({Program}) = 0, ${programConditions}))`;
      } else {
        // Client has no programs, only show general FAQs (no program assignment)
        faqFilterFormula = `AND({Status} = "Published", LEN({Program}) = 0)`;
      }
      
      const faqRecords = await operationsBase(TABLES.FAQS)
        .select({
          filterByFormula: faqFilterFormula,
          sort: [
            { field: 'Category', direction: 'asc' },
            { field: 'Display Order', direction: 'asc' },
            { field: 'Question', direction: 'asc' }
          ],
          fields: ['Question', 'Answer', 'Category', 'Program', 'Display Order', 'Status']
        })
        .all();

      console.log(`Retrieved ${faqRecords.length} relevant FAQs from Airtable using server-side filtering`);

      // Step 4: Process FAQs (no client-side filtering needed now)
      const processedFAQs = faqRecords.map(record => ({
        id: record.id,
        question: record.get('Question') || '',
        answer: record.get('Answer') || '',
        category: record.get('Category') || 'General',
        programIds: record.get('Program') || [],
        displayOrder: record.get('Display Order') || 999
      }));

      // All FAQs are already relevant due to server-side filtering
      const relevantFAQs = processedFAQs;

      console.log(`Filtered to ${relevantFAQs.length} relevant FAQs for client`);

      // Step 6: Group by category
      const faqsByCategory = relevantFAQs.reduce((acc, faq) => {
        if (!acc[faq.category]) {
          acc[faq.category] = [];
        }
        acc[faq.category].push(faq);
        return acc;
      }, {});

      console.log(`Grouped FAQs into ${Object.keys(faqsByCategory).length} categories:`, Object.keys(faqsByCategory));
      
      return faqsByCategory;
      
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      
      // In development mode, fallback to test data if there's an API error  
      const isDev = (typeof import.meta !== 'undefined' && import.meta.env?.DEV) || process.env.NODE_ENV === 'development';
      
      if (isDev) {
        console.log('🧪 FAQ API Error - Falling back to test data in development mode');
        return generateTestFAQData(clientPrograms);
      }
      
      // Track error but don't break the dashboard
      Sentry.withScope((scope) => {
        scope.setTag('apiOperation', 'fetchFAQsForClient');
        scope.setContext('faqFetchError', {
          clientId: clientId,
          programCount: clientPrograms.length,
          error: error.message
        });
        Sentry.captureException(error);
      });
      
      return {}; // Return empty object on error
    }
  });
};

// External Survey API Functions

/**
 * Fetch external survey imports for a client and program
 * @param {string} clientId - Client record ID
 * @param {string} programId - Program record ID
 * @returns {Array} Array of external survey import records
 */
export const fetchExternalSurveyImports = async (clientId, programId) => {
  return trackApiCall('fetchExternalSurveyImports', { clientId, programId }, async () => {
    const records = await operationsBase(TABLES.EXTERNAL_SURVEY_IMPORTS)
      .select({
        filterByFormula: `AND({Client} = '${clientId}', {Program} = '${programId}')`,
        sort: [{ field: 'Import Date', direction: 'desc' }]
      })
      .all();

    return records.map(record => ({
      id: record.id,
      importDate: record.get('Import Date'),
      surveyTitle: record.get('Survey Title'),
      sourceType: record.get('Source Type'),
      responseCount: record.get('Response Count'),
      originalFilename: record.get('Original Filename'),
      importedBy: record.get('Imported By'),
      notes: record.get('Import Notes')
    }));
  });
};

/**
 * Fetch external survey data for multiple SOs (via associations)
 * @param {Array} soNumbers - Array of SO numbers
 * @returns {Array} Array of external survey import records associated with these SOs
 */
export const fetchExternalSurveysForSOs = async (soNumbers) => {
  return trackApiCall('fetchExternalSurveysForSOs', { soNumbers }, async () => {
    if (!soNumbers || soNumbers.length === 0) return [];

    // Build formula to match any of the SO numbers
    const soFilter = soNumbers.map(so => `{SO} = '${so}'`).join(', ');
    const formula = `OR(${soFilter})`;

    // Get SO associations
    const associations = await operationsBase(TABLES.EXTERNAL_SURVEY_SO_ASSOCIATIONS)
      .select({
        filterByFormula: formula,
        fields: ['Survey Import', 'SO', 'Association Notes']
      })
      .all();

    if (associations.length === 0) return [];

    // Get unique survey import IDs
    const surveyImportIds = [...new Set(associations.map(assoc => 
      assoc.get('Survey Import')?.[0]
    ).filter(Boolean))];

    // Fetch survey import records
    const surveyImports = await operationsBase(TABLES.EXTERNAL_SURVEY_IMPORTS)
      .select({
        filterByFormula: surveyImportIds.map(id => `RECORD_ID() = '${id}'`).join(' OR '),
        sort: [{ field: 'Import Date', direction: 'desc' }]
      })
      .all();

    return surveyImports.map(record => ({
      id: record.id,
      importDate: record.get('Import Date'),
      surveyTitle: record.get('Survey Title'),
      sourceType: record.get('Source Type'),
      responseCount: record.get('Response Count'),
      client: record.get('Client'),
      program: record.get('Program'),
      associatedSOs: associations
        .filter(assoc => assoc.get('Survey Import')?.[0] === record.id)
        .map(assoc => assoc.get('SO'))
    }));
  });
};

/**
 * Fetch external survey responses for a survey import
 * @param {string} surveyImportId - Survey import record ID
 * @returns {Array} Array of survey response records
 */
export const fetchExternalSurveyResponses = async (surveyImportId) => {
  return trackApiCall('fetchExternalSurveyResponses', { surveyImportId }, async () => {
    const records = await operationsBase(TABLES.EXTERNAL_SURVEY_RESPONSES)
      .select({
        filterByFormula: `{Survey Import} = '${surveyImportId}'`,
        sort: [{ field: 'Response Number', direction: 'asc' }]
      })
      .all();

    return records.map(record => ({
      id: record.id,
      responseId: record.get('Response ID'),
      responseData: record.get('Response Data'),
      responseNumber: record.get('Response Number'),
      responseDate: record.get('Response Date')
    }));
  });
};

/**
 * Fetch scheduled offering details by SO number (simplified version for import components)
 * @param {string} soNumber - SO number to fetch
 * @returns {Object} Scheduled offering details
 */
export const fetchScheduledOffering = async (soNumber) => {
  return trackApiCall('fetchScheduledOffering', { soNumber }, async () => {
    const escapedSoNumber = escapeAirtableString(soNumber);
    const soRecords = await operationsBase(TABLES.SCHEDULED_OFFERINGS)
      .select({
        filterByFormula: `{SO} = "${escapedSoNumber}"`,
        maxRecords: 1,
        fields: ['SO', 'SOID', 'Client Name', 'ProgramName', 'Type', 'SO Status', 'Client']
      })
      .firstPage();

    if (!soRecords || soRecords.length === 0) {
      throw new Error(`Scheduled Offering with SO number "${soNumber}" not found.`);
    }

    const soRecord = soRecords[0];
    return {
      id: soRecord.id,
      so: soRecord.get('SO'),
      soid: soRecord.get('SOID'),
      clientName: soRecord.get('Client Name'),
      programName: soRecord.get('ProgramName'),
      type: soRecord.get('Type'),
      status: soRecord.get('SO Status'),
      clientId: soRecord.get('Client') ? soRecord.get('Client')[0] : null
    };
  });
};

/**
 * Fetch sessions for a specific SO (for import components)
 * @param {string} soNumber - SO number to fetch sessions for
 * @returns {Array} Array of session records
 */
export const fetchSessionsForSO = async (soNumber) => {
  return trackApiCall('fetchSessionsForSO', { soNumber }, async () => {
    const sessionRecords = await operationsBase(TABLES.SESSIONS)
      .select({
        filterByFormula: `{SO (from SOID)} = "${soNumber}"`,
        sort: [{ field: 'Start', direction: 'asc' }],
        fields: ['Session No', 'Session Name', 'Start']
      })
      .all();

    return sessionRecords.map(record => ({
      id: record.id,
      sessionNo: record.get('Session No') || '',
      sessionName: record.get('Session Name') || '',
      startDate: record.get('Start') || ''
    }));
  });
};

/**
 * Helper function to calculate session duration based on program and session naming patterns
 * Uses Avenue's standard session length conventions
 */
const calculateSessionDuration = (programName, sessionName, sessionNo) => {
  const program = programName?.toLowerCase() || '';
  const session = sessionName?.toLowerCase() || '';
  const sessionNumber = sessionNo?.toLowerCase() || '';
  
  // Check for explicit duration indicators
  if (session.includes('3hr') || sessionNumber.includes('3hr') || program.includes('3 hour')) return 3;
  if (session.includes('4hr') || sessionNumber.includes('4hr') || program.includes('4 hour')) return 4;
  if (session.includes('half day') || sessionNumber.includes('half day')) return 4;
  if (session.includes('full day') || sessionNumber.includes('full day')) return 8;
  if (session.includes('8hr') || sessionNumber.includes('8hr') || program.includes('8 hour')) return 8;
  if (session.includes('workshop') || sessionNumber.includes('workshop')) return 2;
  
  // Program-specific defaults based on Avenue's standard programs
  if (program.includes('connected leader') || program.includes('coaching for results')) return 3;
  if (program.includes('strategic career builder') || program.includes('career conversations')) return 2;
  if (program.includes('leadership') && !program.includes('micro')) return 3;
  if (program.includes('micro learning') || program.includes('micro-learning')) return 1;
  
  // Session type defaults
  if (session.includes('webinar') || sessionNumber.includes('webinar')) return 1.5;
  if (session.includes('intro') || session.includes('introduction')) return 1.5;
  if (session.includes('wrap-up') || session.includes('closing')) return 1.5;
  
  // Default assumption for most Avenue sessions
  return 3;
};

/**
 * Fetch facilitator hours data for sales team filtering and analysis
 * @param {Object} filters - Filter options
 * @param {Date} filters.startDate - Start date for filtering
 * @param {Date} filters.endDate - End date for filtering  
 * @param {Array<string>} filters.programs - Program names to filter by
 * @param {Array<string>} filters.facilitators - Facilitator IDs to filter by
 * @param {Array<string>} filters.clients - Client names to filter by
 * @param {Array<string>} filters.soStatus - SO status values to filter by
 * @returns {Promise<Array>} Array of facilitator hours data grouped by SO
 */
export const fetchFacilitatorHours = async (filters = {}) => {
  return trackApiCall('fetchFacilitatorHours', { filters }, async () => {
    try {
      process.env.NODE_ENV === 'development' && console.time('fetchFacilitatorHours-total');
      
      // Step 1: Build date filter formula
      const { startDate, endDate } = filters;
      let dateFilter = '';
      
      if (startDate || endDate) {
        // Use explicit UTC to avoid timezone ambiguity
        const formatDateUTC = (date) => {
          const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
          return utcDate.toISOString().split('T')[0];
        };
        
        if (startDate && endDate) {
          const startDateStr = formatDateUTC(startDate);
          // Make end date inclusive by adding one day for "before" comparison
          const endDatePlusOne = new Date(endDate);
          endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
          const endDateStr = formatDateUTC(endDatePlusOne);
          
          dateFilter = `AND(
            NOT(IS_BEFORE({Start}, DATETIME_PARSE("${startDateStr}", "YYYY-MM-DD"))),
            IS_BEFORE({Start}, DATETIME_PARSE("${endDateStr}", "YYYY-MM-DD"))
          )`;
        } else if (startDate) {
          const startDateStr = formatDateUTC(startDate);
          dateFilter = `NOT(IS_BEFORE({Start}, DATETIME_PARSE("${startDateStr}", "YYYY-MM-DD")))`;
        } else if (endDate) {
          // Make end date inclusive by adding one day for "before" comparison
          const endDatePlusOne = new Date(endDate);
          endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
          const endDateStr = formatDateUTC(endDatePlusOne);
          dateFilter = `IS_BEFORE({Start}, DATETIME_PARSE("${endDateStr}", "YYYY-MM-DD"))`;
        }
      }
      
      // Step 2: Fetch sessions with base filter
      let sessionFilter = 'NOT({Session Status} = "Cancelled")';
      if (dateFilter) {
        sessionFilter = `AND(${sessionFilter}, ${dateFilter})`;
      }
      
      process.env.NODE_ENV === 'development' && console.log('🔄 Fetching sessions for facilitator hours analysis...');
      const sessionRecords = await operationsBase(TABLES.SESSIONS)
        .select({
          filterByFormula: sessionFilter,
          sort: [{ field: 'Start', direction: 'desc' }],
          maxRecords: 1000, // Performance protection
          fields: [
            'Session Name', 'Session No', 'Start', 'SOID', 'SO (from SOID)', 
            'ProgramName (from SOID)', 'PL', 'PL Override', 'Session Status',
            'Client (from SOID)'
          ]
        })
        .all();
      
      process.env.NODE_ENV === 'development' && console.log(`📊 Fetched ${sessionRecords.length} sessions for analysis`);
      
      if (sessionRecords.length === 0) {
        return [];
      }
      
      // Step 3: Get unique identifiers for parallel fetching
      const uniqueSONumbers = [...new Set(sessionRecords
        .map(record => {
          const soNumber = record.get('SO (from SOID)');
          return Array.isArray(soNumber) ? soNumber[0] : soNumber;
        })
        .filter(Boolean)
      )];
      
      const facilitatorIds = [...new Set(sessionRecords
        .flatMap(record => {
          const pl = record.get('PL') || [];
          const plOverride = record.get('PL Override') || [];
          return [...pl, ...plOverride];
        })
        .filter(Boolean)
      )];
      
      // Step 4: Fetch related data with improved rate limiting
      process.env.NODE_ENV === 'development' && console.log('🔄 Fetching related data (SOs, staff, clients) with controlled rate limiting...');
      
      // Batch related data requests to respect rate limits
      let scheduledOfferingsRecords = [];
      let staffRecords = [];
      
      if (uniqueSONumbers.length > 0) {
        // Split large SO queries into smaller batches to avoid formula complexity limits
        const batchSize = 25; // Conservative batch size for Airtable formula limits
        const soBatches = [];
        for (let i = 0; i < uniqueSONumbers.length; i += batchSize) {
          soBatches.push(uniqueSONumbers.slice(i, i + batchSize));
        }
        
        process.env.NODE_ENV === 'development' && console.log(`📊 Processing ${soBatches.length} SO batches with ${uniqueSONumbers.length} total SOs`);
        
        for (const batch of soBatches) {
          const batchRecords = await operationsBase(TABLES.SCHEDULED_OFFERINGS)
            .select({
              filterByFormula: batch.length === 1 
                ? `AND({SO} = "${escapeAirtableString(batch[0])}", NOT({SO Status} = "Cancelled"))` 
                : `AND(OR(${batch.map(so => `{SO} = "${escapeAirtableString(so)}"`).join(',')}), NOT({SO Status} = "Cancelled"))`,
              fields: ['SO', 'SOID', 'Client Name', 'Type', 'SO Status', 'ProgramName', 'Delivery Start', 'Delivery End']
            })
            .all();
          scheduledOfferingsRecords.push(...batchRecords);
          
          // Add delay between batches to respect rate limits
          if (soBatches.indexOf(batch) < soBatches.length - 1) {
            await rateLimitDelay();
          }
        }
      }
      
      if (facilitatorIds.length > 0) {
        // Batch staff queries similarly
        const staffBatchSize = 50; // Staff queries are simpler, can handle more records
        const staffBatches = [];
        for (let i = 0; i < facilitatorIds.length; i += staffBatchSize) {
          staffBatches.push(facilitatorIds.slice(i, i + staffBatchSize));
        }
        
        for (const batch of staffBatches) {
          const batchRecords = await operationsBase('Staff')
            .select({
              filterByFormula: batch.length === 1 
                ? `RECORD_ID() = "${batch[0]}"` 
                : `OR(${batch.map(id => `RECORD_ID() = "${id}"`).join(',')})`,
              fields: ['Name', 'Status', 'Color']
            })
            .all();
          staffRecords.push(...batchRecords);
          
          // Add delay between batches
          if (staffBatches.indexOf(batch) < staffBatches.length - 1) {
            await rateLimitDelay();
          }
        }
      }
      
      process.env.NODE_ENV === 'development' && console.log(`📊 Fetched ${scheduledOfferingsRecords.length} SOs and ${staffRecords.length} facilitators`);
      
      // Step 5: Create lookup maps
      const soInfoMap = scheduledOfferingsRecords.reduce((map, record) => {
        const soNumber = record.get('SO');
        if (soNumber) {
          map[soNumber] = {
            id: record.id,
            clientName: record.get('Client Name'),
            type: record.get('Type'),
            soStatus: record.get('SO Status'),
            programName: record.get('ProgramName'),
            deliveryStart: record.get('Delivery Start'),
            deliveryEnd: record.get('Delivery End')
          };
        }
        return map;
      }, {});
      
      const staffInfoMap = staffRecords.reduce((map, record) => {
        map[record.id] = {
          name: record.get('Name'),
          status: record.get('Status'),
          color: record.get('Color') || 'gray'
        };
        return map;
      }, {});
      
      // Step 6: Apply additional filters and process sessions
      const filteredSessions = sessionRecords.filter(record => {
        const soNumber = record.get('SO (from SOID)');
        const soNumberDisplay = Array.isArray(soNumber) ? soNumber[0] : soNumber;
        const soInfo = soInfoMap[soNumberDisplay];
        
        if (!soInfo) return false;
        
        // Filter by program names
        if (filters.programs && filters.programs.length > 0) {
          const programName = record.get('ProgramName (from SOID)');
          const programDisplayName = Array.isArray(programName) ? programName[0] : programName;
          if (!filters.programs.some(filterProgram => 
            programDisplayName?.toLowerCase().includes(filterProgram.toLowerCase())
          )) {
            return false;
          }
        }
        
        // Filter by client names
        if (filters.clients && filters.clients.length > 0) {
          const clientName = soInfo.clientName;
          if (!filters.clients.some(filterClient => 
            clientName?.toLowerCase().includes(filterClient.toLowerCase())
          )) {
            return false;
          }
        }
        
        // Filter by SO status
        if (filters.soStatus && filters.soStatus.length > 0) {
          if (!filters.soStatus.includes(soInfo.soStatus)) {
            return false;
          }
        }
        
        // Filter by facilitators
        if (filters.facilitators && filters.facilitators.length > 0) {
          const sessionFacilitatorIds = [
            ...(record.get('PL') || []),
            ...(record.get('PL Override') || [])
          ];
          
          if (!sessionFacilitatorIds.some(id => filters.facilitators.includes(id))) {
            return false;
          }
        }
        
        return true;
      });
      
      process.env.NODE_ENV === 'development' && console.log(`📊 Filtered to ${filteredSessions.length} relevant sessions`);
      
      // Step 7: Group sessions by SO and calculate facilitator hours
      const facilitatorHoursBySOMap = {};
      
      filteredSessions.forEach(record => {
        const soNumber = record.get('SO (from SOID)');
        const soNumberDisplay = Array.isArray(soNumber) ? soNumber[0] : soNumber;
        const soInfo = soInfoMap[soNumberDisplay];
        
        if (!soInfo) return;
        
        // Get facilitator information
        const plField = record.get('PL') || [];
        const plOverrideField = record.get('PL Override') || [];
        
        // PL Override takes precedence over regular PL
        const facilitatorIds = plOverrideField.length > 0 ? plOverrideField : plField;
        
        // Calculate session duration
        const programName = record.get('ProgramName (from SOID)');
        const programDisplayName = Array.isArray(programName) ? programName[0] : programName;
        const sessionName = record.get('Session Name');
        const sessionNo = record.get('Session No');
        
        const sessionDuration = calculateSessionDuration(programDisplayName, sessionName, sessionNo);
        
        // Initialize SO entry if not exists
        if (!facilitatorHoursBySOMap[soNumberDisplay]) {
          facilitatorHoursBySOMap[soNumberDisplay] = {
            soNumber: soNumberDisplay,
            clientName: soInfo.clientName || 'Unknown Client',
            programName: programDisplayName || 'Unknown Program',
            type: soInfo.type,
            soStatus: soInfo.soStatus,
            deliveryStart: soInfo.deliveryStart,
            deliveryEnd: soInfo.deliveryEnd,
            totalSessions: 0,
            totalHours: 0,
            facilitatorHours: {},
            facilitators: new Set(),
            sessions: []
          };
        }
        
        const soEntry = facilitatorHoursBySOMap[soNumberDisplay];
        soEntry.totalSessions++;
        soEntry.totalHours += sessionDuration;
        
        // Add session details
        soEntry.sessions.push({
          id: record.id,
          sessionName: sessionName || 'Unnamed Session',
          sessionNo: sessionNo || '',
          startDate: record.get('Start'),
          duration: sessionDuration,
          facilitatorIds: facilitatorIds,
          facilitators: facilitatorIds.map(id => staffInfoMap[id]).filter(Boolean)
        });
        
        // Track facilitator hours for this SO
        facilitatorIds.forEach(facilitatorId => {
          if (!facilitatorId) return;
          
          const facilitatorInfo = staffInfoMap[facilitatorId];
          if (!facilitatorInfo) return;
          
          soEntry.facilitators.add(facilitatorId);
          
          if (!soEntry.facilitatorHours[facilitatorId]) {
            soEntry.facilitatorHours[facilitatorId] = {
              facilitatorId: facilitatorId,
              facilitatorName: facilitatorInfo.name,
              facilitatorColor: facilitatorInfo.color,
              hours: 0,
              sessionCount: 0
            };
          }
          
          soEntry.facilitatorHours[facilitatorId].hours += sessionDuration;
          soEntry.facilitatorHours[facilitatorId].sessionCount++;
        });
      });
      
      // Step 8: Convert to array and add summary data
      const result = Object.values(facilitatorHoursBySOMap).map(soEntry => {
        // Convert sets to arrays and facilitatorHours object to array
        const facilitatorHoursArray = Object.values(soEntry.facilitatorHours);
        const facilitatorsArray = Array.from(soEntry.facilitators).map(id => staffInfoMap[id]).filter(Boolean);
        
        return {
          ...soEntry,
          facilitators: facilitatorsArray,
          facilitatorHours: facilitatorHoursArray,
          primaryFacilitator: facilitatorsArray[0] || { name: 'Unassigned', color: 'gray' }
        };
      });
      
      // Sort by delivery start date (most recent first)
      result.sort((a, b) => {
        const dateA = a.deliveryStart ? new Date(a.deliveryStart) : new Date(0);
        const dateB = b.deliveryStart ? new Date(b.deliveryStart) : new Date(0);
        return dateB - dateA;
      });
      
      process.env.NODE_ENV === 'development' && console.timeEnd('fetchFacilitatorHours-total');
      process.env.NODE_ENV === 'development' && console.log(`✅ Processed ${result.length} SOs with facilitator hours data`);
      
      return result;
      
    } catch (error) {
      console.error('Error fetching facilitator hours:', error);
      
      Sentry.withScope((scope) => {
        scope.setTag('apiOperation', 'fetchFacilitatorHours');
        scope.setContext('facilitatorHoursError', {
          filters: filters,
          error: error.message
        });
        Sentry.captureException(error);
      });
      
      throw error;
    }
  });
};

/**
 * Fetch a specific session by ID
 * @param {string} sessionId - Session record ID
 * @returns {Object} Session record data
 */
export const fetchSessionById = async (sessionId) => {
  return trackApiCall('fetchSessionById', { sessionId }, async () => {
    const sessionRecord = await operationsBase(TABLES.SESSIONS).find(sessionId);
    
    return {
      id: sessionRecord.id,
      sessionNo: sessionRecord.get('Session No') || '',
      sessionName: sessionRecord.get('Session Name') || '',
      description: sessionRecord.get('Session Description') || sessionRecord.get('Session Name') || '',
      start: sessionRecord.get('Start'),
      duration: sessionRecord.get('Duration (minutes)') || 120,
      sessionStatus: sessionRecord.get('Session Status') || 'Scheduled',
      soid: sessionRecord.get('SOID'),
      soNumber: sessionRecord.get('SO (from SOID)'),
      programName: sessionRecord.get('ProgramName (from SOID)'),
      programCode: sessionRecord.get('Program Code'),
      soMaterialsVersion: sessionRecord.get('so_materials_version'),
      masterMaterialsVersion: sessionRecord.get('master_materials_version'),
      masterMaterialsCdnUrls: sessionRecord.get('master_materials_cdn_urls'),
      materialsLastUpdated: sessionRecord.get('materials_last_updated'),
      materialsUpdatedBy: sessionRecord.get('materials_updated_by')
    };
  });
};

// ===== MATERIALS MANAGEMENT API FUNCTIONS =====

/**
 * Material_Versions CRUD Operations
 */

// Create a new material version
export const createMaterialVersion = async (versionData) => {
  return trackApiCall('createMaterialVersion', { versionData: { ...versionData, change_notes: '[masked]' } }, async () => {
    const fields = {
      'version_id': versionData.version_id,
      'created_at': versionData.created_at || new Date().toISOString(),
      'created_by': versionData.created_by,
      'change_notes': versionData.change_notes || '',
      'materials_manifest': versionData.materials_manifest || '[]',
      'materials_count': versionData.materials_count || 0,
      'total_size_bytes': versionData.total_size_bytes || 0,
      'status': versionData.status || 'Active',
      'is_master_current': versionData.is_master_current || false,
      'usage_count': versionData.usage_count || 0,
      'rollback_available': versionData.rollback_available || true
    };

    // Add relationships if provided
    if (versionData.session_template) {
      fields['session_template'] = Array.isArray(versionData.session_template) 
        ? versionData.session_template 
        : [versionData.session_template];
    }
    if (versionData.program) {
      fields['program'] = Array.isArray(versionData.program) 
        ? versionData.program 
        : [versionData.program];
    }
    if (versionData.parent_version_id) {
      fields['parent_version_id'] = versionData.parent_version_id;
    }

    const newRecord = await operationsBase(TABLES.MATERIAL_VERSIONS).create(fields);
    
    return {
      id: newRecord.id,
      version_id: newRecord.get('version_id'),
      created_at: newRecord.get('created_at'),
      created_by: newRecord.get('created_by'),
      change_notes: newRecord.get('change_notes'),
      status: newRecord.get('status'),
      is_master_current: newRecord.get('is_master_current'),
      materials_count: newRecord.get('materials_count'),
      usage_count: newRecord.get('usage_count')
    };
  });
};

// Fetch material versions for a session
export const fetchMaterialVersions = async (sessionId) => {
  return trackApiCall('fetchMaterialVersions', { sessionId }, async () => {
    try {
      const versionRecords = await operationsBase(TABLES.MATERIAL_VERSIONS)
        .select({
          filterByFormula: `FIND("${sessionId}", ARRAYJOIN({session_template}))`,
          sort: [{ field: 'created_at', direction: 'desc' }]
        })
        .all();

      return versionRecords.map(record => ({
        id: record.id,
        version_id: record.get('version_id'),
        created_at: record.get('created_at'),
        created_by: record.get('created_by'),
        change_notes: record.get('change_notes'),
        materials_manifest: record.get('materials_manifest'),
        materials_count: record.get('materials_count'),
        total_size_bytes: record.get('total_size_bytes'),
        status: record.get('status'),
        is_master_current: record.get('is_master_current'),
        usage_count: record.get('usage_count'),
        rollback_available: record.get('rollback_available'),
        parent_version_id: record.get('parent_version_id')
      }));
    } catch (err) {
      console.warn(`Material_Versions table not available yet, using dummy data. Error: ${err.message}`);
      // Return dummy data for development - Materials Management tables will be set up soon
      return [{
        id: 'dummy-session-version-1',
        version_id: 'v1.0.0',
        created_at: new Date().toISOString(),
        created_by: 'System',
        change_notes: 'Initial version (dummy data)',
        materials_manifest: JSON.stringify([]),
        materials_count: 3,
        total_size_bytes: 1024000,
        status: 'active',
        is_master_current: true,
        usage_count: 0,
        rollback_available: false,
        parent_version_id: null
      }];
    }
  });
};

// Fetch current master version for a session
export const fetchCurrentMasterVersion = async (sessionId) => {
  return trackApiCall('fetchCurrentMasterVersion', { sessionId }, async () => {
    try {
      const versionRecords = await operationsBase(TABLES.MATERIAL_VERSIONS)
        .select({
          filterByFormula: `AND(FIND("${sessionId}", ARRAYJOIN({session_template})), {is_master_current} = TRUE())`,
          maxRecords: 1
        })
        .all();

      if (versionRecords.length === 0) {
        return null;
      }

      const record = versionRecords[0];
      return {
        id: record.id,
        version_id: record.get('version_id'),
        created_at: record.get('created_at'),
        created_by: record.get('created_by'),
        change_notes: record.get('change_notes'),
        materials_manifest: record.get('materials_manifest'),
        materials_count: record.get('materials_count'),
        status: record.get('status'),
        usage_count: record.get('usage_count')
      };
    } catch (err) {
      console.warn(`Material_Versions table not available yet, returning null. Error: ${err.message}`);
      // Return null for now - Materials Management tables will be set up soon
      return null;
    }
  });
};

// Update material version (mainly for marking as master current or updating usage stats)
export const updateMaterialVersion = async (versionId, updateData) => {
  return trackApiCall('updateMaterialVersion', { versionId, updateData }, async () => {
    const fields = {};
    
    if (updateData.is_master_current !== undefined) {
      fields['is_master_current'] = updateData.is_master_current;
    }
    if (updateData.usage_count !== undefined) {
      fields['usage_count'] = updateData.usage_count;
    }
    if (updateData.status !== undefined) {
      fields['status'] = updateData.status;
    }
    if (updateData.rollback_available !== undefined) {
      fields['rollback_available'] = updateData.rollback_available;
    }

    await operationsBase(TABLES.MATERIAL_VERSIONS).update(versionId, fields);
    return true;
  });
};

/**
 * File Upload Validation Utilities
 */

// File size limits (in bytes)
const FILE_SIZE_LIMITS = {
  // Document files
  'application/pdf': 50 * 1024 * 1024, // 50MB for PDFs
  'application/msword': 25 * 1024 * 1024, // 25MB for Word docs
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 25 * 1024 * 1024, // 25MB for .docx
  'application/vnd.ms-excel': 10 * 1024 * 1024, // 10MB for Excel files
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 10 * 1024 * 1024, // 10MB for .xlsx
  'application/vnd.ms-powerpoint': 50 * 1024 * 1024, // 50MB for PowerPoint
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 50 * 1024 * 1024, // 50MB for .pptx
  
  // Text files
  'text/plain': 5 * 1024 * 1024, // 5MB for text files
  'text/csv': 10 * 1024 * 1024, // 10MB for CSV files
  'application/rtf': 10 * 1024 * 1024, // 10MB for RTF files
  
  // Archive files
  'application/zip': 100 * 1024 * 1024, // 100MB for ZIP files
  'application/x-rar-compressed': 100 * 1024 * 1024, // 100MB for RAR files
  
  // Image files
  'image/jpeg': 10 * 1024 * 1024, // 10MB for JPEG
  'image/png': 10 * 1024 * 1024, // 10MB for PNG
  'image/gif': 5 * 1024 * 1024, // 5MB for GIF
  
  // Video files (limited)
  'video/mp4': 500 * 1024 * 1024, // 500MB for MP4
  'video/quicktime': 500 * 1024 * 1024, // 500MB for MOV
  
  // Audio files
  'audio/mpeg': 50 * 1024 * 1024, // 50MB for MP3
  'audio/wav': 100 * 1024 * 1024, // 100MB for WAV
};

// Allowed content types for uploaded files
const ALLOWED_CONTENT_TYPES = new Set([
  // Documents
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/rtf',
  
  // Text
  'text/plain',
  'text/csv',
  
  // Archives (for bundled materials)
  'application/zip',
  'application/x-rar-compressed',
  
  // Images
  'image/jpeg',
  'image/png',
  'image/gif',
  
  // Video (limited support)
  'video/mp4',
  'video/quicktime',
  
  // Audio
  'audio/mpeg',
  'audio/wav',
]);

// Default max file size for unknown types
const DEFAULT_MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

/**
 * Validates file upload data for security and size constraints
 * @param {Object} fileData - File data object
 * @param {string} fileData.content_type - MIME type of the file
 * @param {number} fileData.file_size_bytes - Size of the file in bytes
 * @param {string} fileData.original_filename - Original filename
 * @returns {Object} Validation result with passed boolean and error message if failed
 */
export const validateFileUpload = (fileData) => {
  const { content_type, file_size_bytes, original_filename } = fileData;
  
  // Validate required fields
  if (!content_type) {
    return {
      passed: false,
      error: 'Content type is required for file validation',
      code: 'MISSING_CONTENT_TYPE'
    };
  }
  
  if (file_size_bytes === undefined || file_size_bytes === null) {
    return {
      passed: false,
      error: 'File size is required for file validation',
      code: 'MISSING_FILE_SIZE'
    };
  }
  
  if (!original_filename) {
    return {
      passed: false,
      error: 'Original filename is required for file validation',
      code: 'MISSING_FILENAME'
    };
  }
  
  // Validate content type is allowed
  if (!ALLOWED_CONTENT_TYPES.has(content_type)) {
    return {
      passed: false,
      error: `File type '${content_type}' is not allowed. Please upload a supported file format.`,
      code: 'INVALID_CONTENT_TYPE',
      allowedTypes: Array.from(ALLOWED_CONTENT_TYPES)
    };
  }
  
  // Validate file size
  const maxSize = FILE_SIZE_LIMITS[content_type] || DEFAULT_MAX_FILE_SIZE;
  if (file_size_bytes > maxSize) {
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));
    const fileSizeMB = Math.round(file_size_bytes / (1024 * 1024) * 10) / 10;
    
    return {
      passed: false,
      error: `File size (${fileSizeMB}MB) exceeds maximum allowed size of ${maxSizeMB}MB for ${content_type} files.`,
      code: 'FILE_TOO_LARGE',
      maxSizeBytes: maxSize,
      fileSizeBytes: file_size_bytes
    };
  }
  
  // Basic filename security check
  const dangerousPatterns = /\.\.|[<>:"|?*]/;
  if (dangerousPatterns.test(original_filename)) {
    return {
      passed: false,
      error: 'Filename contains potentially unsafe characters. Please rename the file.',
      code: 'UNSAFE_FILENAME'
    };
  }
  
  // All validations passed
  return {
    passed: true,
    maxSizeBytes: maxSize,
    contentTypeValid: true
  };
};

/**
 * Material_Files CRUD Operations
 */

// Create a new material file record
export const createMaterialFile = async (fileData) => {
  return trackApiCall('createMaterialFile', { fileData: { ...fileData, original_filename: '[masked]' } }, async () => {
    // Validate file upload data before processing
    const validation = validateFileUpload(fileData);
    if (!validation.passed) {
      throw new Error(`File upload validation failed: ${validation.error}`);
    }
    const fields = {
      'file_uuid': fileData.file_uuid,
      'original_filename': fileData.original_filename,
      'display_name': fileData.display_name || fileData.original_filename,
      'file_extension': fileData.file_extension,
      'content_type': fileData.content_type,
      'file_size_bytes': fileData.file_size_bytes || 0,
      'storage_path': fileData.storage_path,
      'cdn_url': fileData.cdn_url,
      'r2_object_key': fileData.r2_object_key,
      'short_code': fileData.short_code,
      'cdn_cache_settings': fileData.cdn_cache_settings || '{}',
      'uploaded_at': fileData.uploaded_at || new Date().toISOString(),
      'uploaded_by': fileData.uploaded_by,
      'download_count': fileData.download_count || 0,
      'unique_downloaders': fileData.unique_downloaders || 0,
      'status': fileData.status || 'Active',
      'is_public': fileData.is_public || false
    };

    // Add relationships
    if (fileData.version) {
      fields['version'] = Array.isArray(fileData.version) ? fileData.version : [fileData.version];
    }
    if (fileData.session) {
      fields['session'] = Array.isArray(fileData.session) ? fileData.session : [fileData.session];
    }
    if (fileData.program) {
      fields['program'] = Array.isArray(fileData.program) ? fileData.program : [fileData.program];
    }

    const newRecord = await operationsBase(TABLES.MATERIAL_FILES).create(fields);
    
    return {
      id: newRecord.id,
      file_uuid: newRecord.get('file_uuid'),
      display_name: newRecord.get('display_name'),
      file_size_bytes: newRecord.get('file_size_bytes'),
      cdn_url: newRecord.get('cdn_url'),
      short_code: newRecord.get('short_code'),
      status: newRecord.get('status'),
      uploaded_at: newRecord.get('uploaded_at')
    };
  });
};

// Fetch material files for a version
export const fetchMaterialFiles = async (versionId) => {
  return trackApiCall('fetchMaterialFiles', { versionId }, async () => {
    try {
      const fileRecords = await operationsBase(TABLES.MATERIAL_FILES)
        .select({
          filterByFormula: `FIND("${versionId}", ARRAYJOIN({version}))`,
          sort: [{ field: 'display_name', direction: 'asc' }]
        })
        .all();

      return fileRecords.map(record => ({
        id: record.id,
        file_uuid: record.get('file_uuid'),
        original_filename: record.get('original_filename'),
        display_name: record.get('display_name'),
        file_extension: record.get('file_extension'),
        content_type: record.get('content_type'),
        file_size_bytes: record.get('file_size_bytes'),
        cdn_url: record.get('cdn_url'),
        short_code: record.get('short_code'),
        uploaded_at: record.get('uploaded_at'),
        uploaded_by: record.get('uploaded_by'),
        download_count: record.get('download_count'),
        status: record.get('status'),
        is_public: record.get('is_public')
      }));
    } catch (err) {
      console.warn(`Material_Files table not available yet, using dummy data. Error: ${err.message}`);
      // Return dummy data for development - Materials Management tables will be set up soon
      return [
        {
          id: 'dummy-file-1',
          file_uuid: 'dummy-uuid-1',
          original_filename: 'Session Guide.pdf',
          display_name: 'Session Guide',
          file_extension: 'pdf',
          content_type: 'application/pdf',
          file_size_bytes: 512000,
          cdn_url: 'https://example.com/dummy-file.pdf',
          short_code: 'ABC123',
          uploaded_at: new Date().toISOString(),
          uploaded_by: 'System',
          download_count: 0,
          status: 'active',
          is_public: false
        },
        {
          id: 'dummy-file-2',
          file_uuid: 'dummy-uuid-2',
          original_filename: 'Workbook.xlsx',
          display_name: 'Participant Workbook',
          file_extension: 'xlsx',
          content_type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          file_size_bytes: 256000,
          cdn_url: 'https://example.com/dummy-workbook.xlsx',
          short_code: 'DEF456',
          uploaded_at: new Date().toISOString(),
          uploaded_by: 'System',
          download_count: 0,
          status: 'active',
          is_public: false
        }
      ];
    }
  });
};

// Fetch material file by short code (for short link resolution)
export const fetchMaterialFileByShortCode = async (shortCode) => {
  return trackApiCall('fetchMaterialFileByShortCode', { shortCode }, async () => {
    const fileRecords = await operationsBase(TABLES.MATERIAL_FILES)
      .select({
        filterByFormula: `{short_code} = "${shortCode}"`,
        maxRecords: 1
      })
      .all();

    if (fileRecords.length === 0) {
      return null;
    }

    const record = fileRecords[0];
    return {
      id: record.id,
      file_uuid: record.get('file_uuid'),
      display_name: record.get('display_name'),
      cdn_url: record.get('cdn_url'),
      content_type: record.get('content_type'),
      file_size_bytes: record.get('file_size_bytes'),
      status: record.get('status'),
      is_public: record.get('is_public')
    };
  });
};

// Update material file (mainly for download tracking)
export const updateMaterialFile = async (fileId, updateData) => {
  return trackApiCall('updateMaterialFile', { fileId, updateData }, async () => {
    const fields = {};
    
    if (updateData.download_count !== undefined) {
      fields['download_count'] = updateData.download_count;
    }
    if (updateData.unique_downloaders !== undefined) {
      fields['unique_downloaders'] = updateData.unique_downloaders;
    }
    if (updateData.last_downloaded_at !== undefined) {
      fields['last_downloaded_at'] = updateData.last_downloaded_at;
    }
    if (updateData.status !== undefined) {
      fields['status'] = updateData.status;
    }

    await operationsBase(TABLES.MATERIAL_FILES).update(fileId, fields);
    return true;
  });
};

// Increment download count for a file (atomic operation)
export const incrementMaterialFileDownloadCount = async (fileId) => {
  return trackApiCall('incrementMaterialFileDownloadCount', { fileId }, async () => {
    // First fetch current count
    const record = await operationsBase(TABLES.MATERIAL_FILES).find(fileId);
    const currentCount = record.get('download_count') || 0;
    
    // Increment and update
    await operationsBase(TABLES.MATERIAL_FILES).update(fileId, {
      'download_count': currentCount + 1,
      'last_downloaded_at': new Date().toISOString()
    });
    
    return currentCount + 1;
  });
};

/**
 * IP Hashing Utility
 */

/**
 * Hash an IP address for privacy while maintaining some tracking capability
 * Uses a simple but effective hash that:
 * 1. Obscures the original IP
 * 2. Maintains consistency (same IP = same hash)
 * 3. Provides some geographic/network diversity preservation
 * @param {string} ip - IP address to hash
 * @returns {string} Hashed IP address
 */
export const hashIpAddress = (ip) => {
  if (!ip || typeof ip !== 'string') {
    return 'unknown';
  }
  
  // Simple hash function for IP addresses
  // This provides privacy while maintaining some analytics value
  let hash = 0;
  for (let i = 0; i < ip.length; i++) {
    const char = ip.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Convert to positive hex string and truncate for privacy
  const hashStr = Math.abs(hash).toString(16).padStart(8, '0');
  
  // For IPv4, preserve network class info
  if (ip.includes('.')) {
    const firstOctet = ip.split('.')[0];
    const networkClass = getNetworkClass(parseInt(firstOctet));
    return `${networkClass}-${hashStr.substring(0, 8)}`;
  }
  
  // For IPv6 or other formats, just use hash
  return `ip-${hashStr.substring(0, 8)}`;
};

/**
 * Get network class for IPv4 address (preserves some geographic/ISP info)
 * @param {number} firstOctet - First octet of IPv4 address
 * @returns {string} Network class identifier
 */
const getNetworkClass = (firstOctet) => {
  if (firstOctet >= 1 && firstOctet <= 126) return 'A';
  if (firstOctet >= 128 && firstOctet <= 191) return 'B';
  if (firstOctet >= 192 && firstOctet <= 223) return 'C';
  if (firstOctet >= 224 && firstOctet <= 239) return 'D';
  if (firstOctet >= 240 && firstOctet <= 255) return 'E';
  return 'X'; // Unknown/invalid
};

/**
 * Material_Access_Logs Tracking Functions
 */

// Create access log entry
export const createMaterialAccessLog = async (accessData) => {
  return trackApiCall('createMaterialAccessLog', { accessData: { ...accessData, user_ip: '[masked]' } }, async () => {
    // Hash IP address for privacy
    const hashedIp = hashIpAddress(accessData.user_ip);
    
    const fields = {
      'accessed_at': accessData.accessed_at || new Date().toISOString(),
      'access_method': accessData.access_method,
      'user_ip': hashedIp, // IP is now properly hashed for privacy
      'user_agent': accessData.user_agent || '',
      'session_id': accessData.session_id || '',
      'country_code': accessData.country_code || '',
      'cdn_cache_status': accessData.cdn_cache_status || '',
      'download_duration_ms': accessData.download_duration_ms || 0,
      'referrer_url': accessData.referrer_url || '',
      'access_context': accessData.access_context || '{}'
    };

    // Add relationships
    if (accessData.material_file) {
      fields['material_file'] = Array.isArray(accessData.material_file) 
        ? accessData.material_file 
        : [accessData.material_file];
    }
    if (accessData.so_context) {
      fields['so_context'] = Array.isArray(accessData.so_context) 
        ? accessData.so_context 
        : [accessData.so_context];
    }

    const newRecord = await operationsBase(TABLES.MATERIAL_ACCESS_LOGS).create(fields);
    
    return {
      id: newRecord.id,
      accessed_at: newRecord.get('accessed_at'),
      access_method: newRecord.get('access_method'),
      country_code: newRecord.get('country_code')
    };
  });
};

// Fetch access logs for a file (with pagination)
export const fetchMaterialAccessLogs = async (fileId, options = {}) => {
  return trackApiCall('fetchMaterialAccessLogs', { fileId, options }, async () => {
    const { maxRecords = 100, offset = 0 } = options;
    
    const accessRecords = await operationsBase(TABLES.MATERIAL_ACCESS_LOGS)
      .select({
        filterByFormula: `FIND("${fileId}", ARRAYJOIN({material_file}))`,
        sort: [{ field: 'accessed_at', direction: 'desc' }],
        maxRecords,
        offset
      })
      .all();

    return accessRecords.map(record => ({
      id: record.id,
      accessed_at: record.get('accessed_at'),
      access_method: record.get('access_method'),
      country_code: record.get('country_code'),
      cdn_cache_status: record.get('cdn_cache_status'),
      download_duration_ms: record.get('download_duration_ms'),
      referrer_url: record.get('referrer_url')
    }));
  });
};

/**
 * Material_Bulk_Operations Tracking Functions
 */

// Create bulk operation record
export const createMaterialBulkOperation = async (operationData) => {
  return trackApiCall('createMaterialBulkOperation', { operationData }, async () => {
    const fields = {
      'operation_type': operationData.operation_type,
      'files_affected': operationData.files_affected || 0,
      'sos_impacted': operationData.sos_impacted || 0,
      'started_at': operationData.started_at || new Date().toISOString(),
      'performed_by': operationData.performed_by,
      'status': operationData.status || 'In Progress',
      'success_count': operationData.success_count || 0,
      'failure_count': operationData.failure_count || 0,
      'error_details': operationData.error_details || '[]',
      'can_rollback': operationData.can_rollback || false,
      'rollback_data': operationData.rollback_data || '{}',
      'rollback_performed': operationData.rollback_performed || false
    };

    // Add relationships
    if (operationData.affected_sessions) {
      fields['affected_sessions'] = Array.isArray(operationData.affected_sessions) 
        ? operationData.affected_sessions 
        : [operationData.affected_sessions];
    }
    if (operationData.affected_programs) {
      fields['affected_programs'] = Array.isArray(operationData.affected_programs) 
        ? operationData.affected_programs 
        : [operationData.affected_programs];
    }

    const newRecord = await operationsBase(TABLES.MATERIAL_BULK_OPERATIONS).create(fields);
    
    return {
      id: newRecord.id,
      operation_type: newRecord.get('operation_type'),
      started_at: newRecord.get('started_at'),
      status: newRecord.get('status')
    };
  });
};

// Update bulk operation (mainly for completion tracking)
export const updateMaterialBulkOperation = async (operationId, updateData) => {
  return trackApiCall('updateMaterialBulkOperation', { operationId, updateData }, async () => {
    const fields = {};
    
    if (updateData.completed_at !== undefined) {
      fields['completed_at'] = updateData.completed_at;
    }
    if (updateData.duration_seconds !== undefined) {
      fields['duration_seconds'] = updateData.duration_seconds;
    }
    if (updateData.status !== undefined) {
      fields['status'] = updateData.status;
    }
    if (updateData.success_count !== undefined) {
      fields['success_count'] = updateData.success_count;
    }
    if (updateData.failure_count !== undefined) {
      fields['failure_count'] = updateData.failure_count;
    }
    if (updateData.error_details !== undefined) {
      fields['error_details'] = updateData.error_details;
    }

    await operationsBase(TABLES.MATERIAL_BULK_OPERATIONS).update(operationId, fields);
    return true;
  });
};

/**
 * Version Inheritance and Override Functions
 */

// Inherit materials for new SO (locks current master versions)
export const inheritMaterialsForSO = async (soId, sessionIds) => {
  return trackApiCall('inheritMaterialsForSO', { soId, sessionIds }, async () => {
    // Validate soId is an Airtable record ID format
    if (!soId || !soId.startsWith('rec') || soId.length !== 17) {
      throw new Error(`Invalid Airtable record ID format for soId: ${soId}. Expected format: rec followed by 14 characters.`);
    }
    const inheritanceResults = [];
    
    for (const sessionId of sessionIds) {
      // Get current master version for this session
      const masterVersion = await fetchCurrentMasterVersion(sessionId);
      
      if (masterVersion) {
        // Update the session record with SO-specific version lock
        const sessionRecord = await operationsBase(TABLES.SESSIONS).find(sessionId);
        
        await operationsBase(TABLES.SESSIONS).update(sessionId, {
          'so_materials_version': masterVersion.version_id,
          'so_materials_manifest': masterVersion.materials_manifest,
          'so_materials_cdn_urls': sessionRecord.get('master_materials_cdn_urls') || '[]'
        });
        
        inheritanceResults.push({
          sessionId,
          versionId: masterVersion.version_id,
          materialsCount: masterVersion.materials_count
        });
      }
    }

    // Update SO-level tracking
    await operationsBase(TABLES.SCHEDULED_OFFERINGS).update(soId, {
      'materials_inherited_at': new Date().toISOString(),
      'materials_locked_version': inheritanceResults.length > 0 
        ? inheritanceResults[0].versionId 
        : 'v1.0.0',
      'has_custom_materials': false
    });

    return inheritanceResults;
  });
};

// Update master materials for a session (creates new version)
export const updateMasterMaterials = async (sessionId, versionData, filesList = []) => {
  return trackApiCall('updateMasterMaterials', { sessionId, versionData: { ...versionData, change_notes: '[masked]' } }, async () => {
    // 1. Create new version
    const newVersion = await createMaterialVersion({
      ...versionData,
      session_template: sessionId,
      is_master_current: false // Will be set after successful creation
    });

    // 2. Update session master pointers
    const cdnUrls = filesList.map(file => file.cdn_url || '');
    const materialsManifest = JSON.stringify(filesList.map(file => ({
      file_uuid: file.file_uuid,
      display_name: file.display_name,
      cdn_url: file.cdn_url,
      file_size_bytes: file.file_size_bytes
    })));

    await operationsBase(TABLES.SESSIONS).update(sessionId, {
      'master_materials_version': newVersion.version_id,
      'master_materials_cdn_urls': JSON.stringify(cdnUrls),
      'master_materials_manifest': materialsManifest,
      'materials_last_updated': new Date().toISOString(),
      'materials_updated_by': versionData.created_by
    });

    // 3. Mark previous master as non-current
    const previousMaster = await fetchCurrentMasterVersion(sessionId);
    if (previousMaster && previousMaster.id !== newVersion.id) {
      await updateMaterialVersion(previousMaster.id, { is_master_current: false });
    }

    // 4. Mark new version as current master
    await updateMaterialVersion(newVersion.id, { is_master_current: true });

    return {
      versionId: newVersion.version_id,
      newVersionId: newVersion.id,
      filesCount: filesList.length,
      cdnUrls
    };
  });
};

// Override materials for specific SO (creates session-level override)
export const overrideMaterialsForSO = async (sessionId, soId, overrideData) => {
  return trackApiCall('overrideMaterialsForSO', { sessionId, soId, overrideData }, async () => {
    // Update session with SO-specific override
    const cdnUrls = overrideData.filesList?.map(file => file.cdn_url || '') || [];
    const materialsManifest = JSON.stringify(overrideData.filesList?.map(file => ({
      file_uuid: file.file_uuid,
      display_name: file.display_name,
      cdn_url: file.cdn_url,
      file_size_bytes: file.file_size_bytes
    })) || []);

    await operationsBase(TABLES.SESSIONS).update(sessionId, {
      'so_materials_version': overrideData.version_id || 'custom',
      'so_materials_manifest': materialsManifest,
      'so_materials_cdn_urls': JSON.stringify(cdnUrls)
    });

    // Mark SO as having custom materials
    await operationsBase(TABLES.SCHEDULED_OFFERINGS).update(soId, {
      'has_custom_materials': true
    });

    return {
      sessionId,
      soId,
      overrideVersionId: overrideData.version_id,
      filesCount: overrideData.filesList?.length || 0
    };
  });
};

// Fetch materials for SO session (respects inheritance and overrides)
export const fetchMaterialsForSOSession = async (sessionId, soId) => {
  return trackApiCall('fetchMaterialsForSOSession', { sessionId, soId }, async () => {
    // Get session record with materials data
    const sessionRecord = await operationsBase(TABLES.SESSIONS).find(sessionId);
    
    // Check if SO has overrides
    const soMaterialsVersion = sessionRecord.get('so_materials_version');
    const soMaterialsManifest = sessionRecord.get('so_materials_manifest');
    
    let materials = [];
    let versionInfo = {};
    
    if (soMaterialsVersion && soMaterialsManifest) {
      // Use SO-specific materials (inherited or overridden)
      try {
        materials = JSON.parse(soMaterialsManifest);
        versionInfo = {
          version_id: soMaterialsVersion,
          type: 'so_specific',
          cdn_urls: JSON.parse(sessionRecord.get('so_materials_cdn_urls') || '[]')
        };
      } catch (error) {
        console.error('Error parsing SO materials manifest:', error);
        materials = [];
      }
    } else {
      // Fall back to master materials
      const masterManifest = sessionRecord.get('master_materials_manifest');
      const masterVersion = sessionRecord.get('master_materials_version');
      
      if (masterManifest) {
        try {
          materials = JSON.parse(masterManifest);
          versionInfo = {
            version_id: masterVersion,
            type: 'master',
            cdn_urls: JSON.parse(sessionRecord.get('master_materials_cdn_urls') || '[]')
          };
        } catch (error) {
          console.error('Error parsing master materials manifest:', error);
          materials = [];
        }
      }
    }

    return {
      sessionId,
      soId,
      materials,
      versionInfo,
      materialsCount: materials.length,
      lastUpdated: sessionRecord.get('materials_last_updated')
    };
  });
};

// ===================================
// PROGRAM-LEVEL MATERIALS MANAGEMENT API
// ===================================

// Program Materials CRUD
export const fetchProgramMaterials = async (programId) => {
  return trackApiCall('fetchProgramMaterials', { programId }, async () => {
    const programRecord = await operationsBase(TABLES.PROGRAMS).find(programId);
    
    return {
      programId,
      masterMaterialsVersion: programRecord.get('master_materials_version'),
      materialsManifest: programRecord.get('materials_manifest'),
      materialsCdnUrls: programRecord.get('materials_cdn_urls'),
      materialsLastUpdated: programRecord.get('materials_last_updated'),
      materialsUpdatedBy: programRecord.get('materials_updated_by'),
      materialsTemplateLocked: programRecord.get('materials_template_locked'),
      activeMaterialFiles: programRecord.get('active_material_files')
    };
  });
};

export const updateProgramMasterMaterials = async (programId, materialData) => {
  return trackApiCall('updateProgramMasterMaterials', { programId, materialData: { ...materialData, change_notes: '[masked]' } }, async () => {
    // 1. Create new version for the program
    const newVersion = await createMaterialVersion({
      ...materialData.versionData,
      is_master_current: false // Will be set after successful creation
    });

    // 2. Update program master pointers
    const cdnUrls = materialData.filesList?.map(file => file.cdn_url || '') || [];
    const materialsManifest = JSON.stringify((materialData.filesList || []).map(file => ({
      file_uuid: file.file_uuid,
      display_name: file.display_name,
      cdn_url: file.cdn_url,
      file_size_bytes: file.file_size_bytes
    })));

    await operationsBase(TABLES.PROGRAMS).update(programId, {
      'master_materials_version': Array.isArray(newVersion.id) ? newVersion.id : [newVersion.id],
      'materials_manifest': materialsManifest,
      'materials_cdn_urls': JSON.stringify(cdnUrls),
      'materials_last_updated': new Date().toISOString(),
      'materials_updated_by': materialData.versionData.created_by,
      'active_material_files': materialData.activeMaterialFiles || []
    });

    // 3. Mark previous version as no longer current (if exists)
    const previousMaster = await fetchCurrentProgramMasterVersion(programId);
    if (previousMaster && previousMaster.id !== newVersion.id) {
      await updateMaterialVersion(previousMaster.id, { is_master_current: false });
    }

    // 4. Mark new version as current master
    await updateMaterialVersion(newVersion.id, { is_master_current: true });

    return {
      versionId: newVersion.version_id,
      newVersionId: newVersion.id,
      filesCount: materialData.filesList?.length || 0,
      cdnUrls
    };
  });
};

export const lockProgramTemplate = async (programId) => {
  return trackApiCall('lockProgramTemplate', { programId }, async () => {
    await operationsBase(TABLES.PROGRAMS).update(programId, {
      'materials_template_locked': true
    });
    
    return {
      programId,
      locked: true,
      lockedAt: new Date().toISOString()
    };
  });
};

// Program-to-Session Inheritance
export const inheritProgramMaterialsToSession = async (programId, sessionId) => {
  return trackApiCall('inheritProgramMaterialsToSession', { programId, sessionId }, async () => {
    // Get program materials
    const programMaterials = await fetchProgramMaterials(programId);
    
    if (!programMaterials.masterMaterialsVersion) {
      throw new Error('Program has no master materials to inherit');
    }

    // Update session with inherited program materials
    await operationsBase(TABLES.SESSIONS).update(sessionId, {
      'master_materials_version': programMaterials.masterMaterialsVersion,
      'master_materials_manifest': programMaterials.materialsManifest,
      'master_materials_cdn_urls': programMaterials.materialsCdnUrls,
      'materials_last_updated': new Date().toISOString(),
      'materials_updated_by': programMaterials.materialsUpdatedBy
    });

    return {
      programId,
      sessionId,
      inheritedVersion: Array.isArray(programMaterials.masterMaterialsVersion) 
        ? programMaterials.masterMaterialsVersion[0] 
        : programMaterials.masterMaterialsVersion,
      materialsCount: programMaterials.materialsManifest ? 
        JSON.parse(programMaterials.materialsManifest).length : 0
    };
  });
};

export const syncSessionsWithProgramMaterials = async (programId) => {
  return trackApiCall('syncSessionsWithProgramMaterials', { programId }, async () => {
    // Get all sessions for this program
    const sessionsRecords = await operationsBase(TABLES.SESSIONS)
      .select({
        filterByFormula: `FIND("${programId}", ARRAYJOIN({Program}))`,
        fields: ['Session Name', 'Program']
      })
      .all();

    const syncResults = [];
    
    for (const sessionRecord of sessionsRecords) {
      const sessionId = sessionRecord.id;
      try {
        const result = await inheritProgramMaterialsToSession(programId, sessionId);
        syncResults.push({
          sessionId,
          sessionName: sessionRecord.get('Session Name'),
          success: true,
          inheritedVersion: result.inheritedVersion
        });
      } catch (error) {
        syncResults.push({
          sessionId,
          sessionName: sessionRecord.get('Session Name'),
          success: false,
          error: error.message
        });
      }
    }

    return {
      programId,
      sessionsUpdated: syncResults.filter(r => r.success).length,
      sessionsFailed: syncResults.filter(r => !r.success).length,
      results: syncResults
    };
  });
};

// Program Materials Versioning
export const createProgramMaterialVersion = async (programId, versionData) => {
  return trackApiCall('createProgramMaterialVersion', { programId, versionData: { ...versionData, change_notes: '[masked]' } }, async () => {
    const newVersion = await createMaterialVersion({
      ...versionData,
      is_master_current: false // Will be updated separately
    });

    // Link version to program (if not already linked)
    const programRecord = await operationsBase(TABLES.PROGRAMS).find(programId);
    const currentVersions = programRecord.get('master_materials_version') || [];
    const versionIds = Array.isArray(currentVersions) ? currentVersions : [currentVersions];
    
    if (!versionIds.includes(newVersion.id)) {
      versionIds.push(newVersion.id);
      await operationsBase(TABLES.PROGRAMS).update(programId, {
        'master_materials_version': versionIds
      });
    }

    return {
      programId,
      versionId: newVersion.version_id,
      versionRecordId: newVersion.id,
      materialsCount: versionData.materials_count || 0
    };
  });
};

export const fetchProgramMaterialVersions = async (programId) => {
  return trackApiCall('fetchProgramMaterialVersions', { programId }, async () => {
    // Get program to find linked versions
    const programRecord = await operationsBase(TABLES.PROGRAMS).find(programId);
    const linkedVersions = programRecord.get('master_materials_version') || [];
    
    if (linkedVersions.length === 0) {
      return {
        programId,
        versions: [],
        totalVersions: 0
      };
    }

    // Build filter formula for linked versions
    const versionIds = Array.isArray(linkedVersions) ? linkedVersions : [linkedVersions];
    const filterFormula = `OR(${versionIds.map(id => `RECORD_ID() = "${id}"`).join(', ')})`;

    let versions = [];
    try {
      const versionRecords = await operationsBase(TABLES.MATERIAL_VERSIONS)
        .select({
          filterByFormula: filterFormula,
          sort: [{ field: 'created_at', direction: 'desc' }]
        })
        .all();

      versions = versionRecords.map(record => ({
        id: record.id,
        version_id: record.get('version_id'),
        created_at: record.get('created_at'),
        created_by: record.get('created_by'),
        change_notes: record.get('change_notes'),
        materials_count: record.get('materials_count'),
        status: record.get('status'),
        is_master_current: record.get('is_master_current'),
        usage_count: record.get('usage_count')
      }));
    } catch (err) {
      console.warn(`Material_Versions table not available yet, using dummy data. Error: ${err.message}`);
      // Return dummy data for development - Materials Management tables will be set up soon
      versions = [{
        id: 'dummy-version-1',
        version_id: 'v1.0.0',
        created_at: new Date().toISOString(),
        created_by: 'System',
        change_notes: 'Initial version (dummy data)',
        materials_count: 3,
        status: 'active',
        is_master_current: true,
        usage_count: 0
      }];
    }

    return {
      programId,
      versions,
      totalVersions: versions.length,
      currentMaster: versions.find(v => v.is_master_current)
    };
  });
};

// Helper function to fetch current program master version
export const fetchCurrentProgramMasterVersion = async (programId) => {
  return trackApiCall('fetchCurrentProgramMasterVersion', { programId }, async () => {
    const programRecord = await operationsBase(TABLES.PROGRAMS).find(programId);
    const linkedVersions = programRecord.get('master_materials_version') || [];
    
    if (linkedVersions.length === 0) {
      return null;
    }

    // Build filter for current master among linked versions
    const versionIds = Array.isArray(linkedVersions) ? linkedVersions : [linkedVersions];
    const filterFormula = `AND(OR(${versionIds.map(id => `RECORD_ID() = "${id}"`).join(', ')}), {is_master_current} = TRUE())`;

    const versionRecords = await operationsBase(TABLES.MATERIAL_VERSIONS)
      .select({
        filterByFormula: filterFormula,
        maxRecords: 1
      })
      .all();

    if (versionRecords.length === 0) {
      return null;
    }

    const record = versionRecords[0];
    return {
      id: record.id,
      version_id: record.get('version_id'),
      created_at: record.get('created_at'),
      created_by: record.get('created_by'),
      change_notes: record.get('change_notes'),
      materials_manifest: record.get('materials_manifest'),
      materials_count: record.get('materials_count'),
      total_size_bytes: record.get('total_size_bytes'),
      status: record.get('status'),
      is_master_current: record.get('is_master_current')
    };
  });
};

// Three-Tier Flow Management
export const fetchMaterialsHierarchy = async (programId) => {
  return trackApiCall('fetchMaterialsHierarchy', { programId }, async () => {
    // Get program materials
    const programMaterials = await fetchProgramMaterials(programId);
    
    // Get all sessions for this program
    const sessionsRecords = await operationsBase(TABLES.SESSIONS)
      .select({
        filterByFormula: `FIND("${programId}", ARRAYJOIN({Program}))`,
        fields: [
          'Session Name', 
          'Program',
          'master_materials_version',
          'master_materials_manifest',
          'so_materials_version',
          'so_materials_manifest',
          'materials_last_updated'
        ]
      })
      .all();

    const sessions = [];
    
    for (const sessionRecord of sessionsRecords) {
      const sessionId = sessionRecord.id;
      
      // Get SOs for this session
      const soRecords = await operationsBase(TABLES.SCHEDULED_OFFERINGS)
        .select({
          filterByFormula: `FIND("${sessionId}", ARRAYJOIN({Sessions}))`,
          fields: [
            'SOID',
            'Client',
            'so_materials_version',
            'so_materials_manifest'
          ]
        })
        .all();

      const sos = soRecords.map(soRecord => ({
        id: soRecord.id,
        soId: soRecord.get('SOID'),
        client: soRecord.get('Client'),
        hasMaterialsOverride: !!soRecord.get('so_materials_version'),
        materialsVersion: soRecord.get('so_materials_version'),
        materialsCount: soRecord.get('so_materials_manifest') ? 
          JSON.parse(soRecord.get('so_materials_manifest')).length : 0
      }));

      sessions.push({
        id: sessionId,
        sessionName: sessionRecord.get('Session Name'),
        hasMasterMaterials: !!sessionRecord.get('master_materials_version'),
        masterMaterialsVersion: sessionRecord.get('master_materials_version'),
        materialsLastUpdated: sessionRecord.get('materials_last_updated'),
        materialsCount: sessionRecord.get('master_materials_manifest') ? 
          JSON.parse(sessionRecord.get('master_materials_manifest')).length : 0,
        scheduledOfferings: sos
      });
    }

    return {
      programId,
      programMaterials: {
        hasManagerMaterials: !!programMaterials.masterMaterialsVersion,
        materialsVersion: programMaterials.masterMaterialsVersion,
        materialsCount: programMaterials.materialsManifest ? 
          JSON.parse(programMaterials.materialsManifest).length : 0,
        lastUpdated: programMaterials.materialsLastUpdated,
        isLocked: programMaterials.materialsTemplateLocked
      },
      sessions,
      totalSessions: sessions.length,
      totalSOs: sessions.reduce((sum, session) => sum + session.scheduledOfferings.length, 0)
    };
  });
};

export const analyzeProgramMaterialsImpact = async (programId) => {
  return trackApiCall('analyzeProgramMaterialsImpact', { programId }, async () => {
    const hierarchy = await fetchMaterialsHierarchy(programId);
    
    // Analysis of impact
    const sessionsWithMaterials = hierarchy.sessions.filter(s => s.hasMasterMaterials).length;
    const sessionsWithoutMaterials = hierarchy.sessions.length - sessionsWithMaterials;
    
    const sosWithOverrides = hierarchy.sessions.reduce((total, session) => {
      return total + session.scheduledOfferings.filter(so => so.hasMaterialsOverride).length;
    }, 0);
    
    const totalSOs = hierarchy.totalSOs;
    const sosInheritingFromSessions = totalSOs - sosWithOverrides;

    return {
      programId,
      impact: {
        totalSessions: hierarchy.totalSessions,
        sessionsWithMaterials,
        sessionsWithoutMaterials,
        totalSOs,
        sosWithOverrides,
        sosInheritingFromSessions,
        wouldInheritFromProgram: sessionsWithoutMaterials > 0 ? 
          hierarchy.sessions
            .filter(s => !s.hasMasterMaterials)
            .reduce((total, session) => total + session.scheduledOfferings.length, 0) : 0
      },
      recommendations: {
        shouldSyncSessions: sessionsWithoutMaterials > 0,
        riskLevel: sosWithOverrides > totalSOs * 0.5 ? 'high' : 
                  sosWithOverrides > totalSOs * 0.2 ? 'medium' : 'low',
        impactDescription: `Updating program materials will affect ${sessionsWithoutMaterials} sessions and ${hierarchy.totalSOs - sosWithOverrides} SOs`
      },
      hierarchy
    };
  });
};

/**
 * Fetch program by ID
 */
export const fetchProgramById = async (programId) => {
  return trackApiCall('fetchProgramById', { programId }, async () => {
    try {
      const record = await operationsBase(TABLES.PROGRAMS).find(programId);
      const fields = record.fields;
      
      return {
        id: record.id,
        programName: fields['Full Program Name'] || fields.name,
        programCode: fields['ProgramID'] || fields.code,
        description: fields.Description || fields.description,
        name: fields['Full Program Name'] || fields.name,
        code: fields['ProgramID'] || fields.code,
        lastModified: fields['Last Modified'] || new Date().toISOString()
      };
    } catch (error) {
      console.error('Error fetching program by ID:', error);
      if (error.statusCode === 404) {
        throw new Error('NOT_FOUND: Program not found');
      }
      throw error;
    }
  });
};

/**
 * Fetch all sessions belonging to a program
 */
export const fetchSessionsByProgram = async (programId) => {
  return trackApiCall('fetchSessionsByProgram', { programId }, async () => {
    try {
      const sessionsResponse = await operationsBase(TABLES.SESSIONS)
        .select({
          filterByFormula: `FIND("${programId}", {Program})`
        })
        .all();

      return sessionsResponse.map(sessionRecord => {
        const fields = sessionRecord.fields;
        
        return {
          id: sessionRecord.id,
          sessionName: fields['Session Name'] || fields.name,
          sessionNo: fields['Session No'] || fields.sessionNumber,
          name: fields['Session Name'] || fields.name,
          materialsSource: 'inherited', // Default - would need to check actual inheritance status
          hasOwnMaterials: false, // Would need to check if session has overrides
          lastMaterialUpdate: fields['Materials Last Updated'] || fields['Last Modified'],
          activeSOs: fields['Active SOs'] || 0,
          materialCount: fields['Material Count'] || 0,
          description: fields.Description || fields.description
        };
      });
    } catch (error) {
      console.error('Error fetching sessions by program:', error);
      throw error;
    }
  });
};

export default {
  fetchProgramBySO,
  fetchSessions,
  fetchAttendees,
  updateAttendanceStatus,
  updateAttendeeNotes,
  updateSessionNotes,
  fetchSessionNotes,
  addAttendee,
  lookupClientByName,
  createAttendanceRecord,
  debugCheckSessionAttendance,
  updateSONotes,
  fetchSONotes,
  searchScheduledOfferings,
  getWeeklyScheduledOfferings,
  getWeeklySessions,
  fetchSessionsForDateRange,
  getActiveClientsWithSOsAndPrograms,
  fetchFAQsForClient,
  fetchExternalSurveyImports,
  fetchExternalSurveysForSOs,
  fetchExternalSurveyResponses,
  fetchScheduledOffering,
  fetchSessionsForSO,
  fetchFacilitatorHours,
  fetchSessionById,
  // File Upload Validation
  validateFileUpload,
  // IP Hashing Utility
  hashIpAddress,
  // Materials Management Functions
  createMaterialVersion,
  fetchMaterialVersions,
  fetchCurrentMasterVersion,
  updateMaterialVersion,
  createMaterialFile,
  fetchMaterialFiles,
  fetchMaterialFileByShortCode,
  updateMaterialFile,
  incrementMaterialFileDownloadCount,
  createMaterialAccessLog,
  fetchMaterialAccessLogs,
  createMaterialBulkOperation,
  updateMaterialBulkOperation,
  inheritMaterialsForSO,
  updateMasterMaterials,
  overrideMaterialsForSO,
  fetchMaterialsForSOSession,
  // Program-Level Materials Management Functions
  fetchProgramMaterials,
  updateProgramMasterMaterials,
  lockProgramTemplate,
  inheritProgramMaterialsToSession,
  syncSessionsWithProgramMaterials,
  createProgramMaterialVersion,
  fetchProgramMaterialVersions,
  fetchCurrentProgramMasterVersion,
  fetchMaterialsHierarchy,
  analyzeProgramMaterialsImpact,
  fetchProgramById,
  fetchSessionsByProgram
};